import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { Message, Role, Coordinates, Place, Location, RouteSuggestion, SavedJourney, OpeningHours } from './types';
import { sendChatMessage, getOrCreateSessionId, getRoute, recalculateRoute, textToSpeech, translateText, getConfig, resetSessionId } from './services/routexApi';
import { ChatMessage, AgentStatusBar } from './components/ChatMessage';
import { BottomNav, DEFAULT_TABS } from './components/BottomNav';
import { GoogleMap } from './components/GoogleMap';
import { LocationInput } from './components/LocationInput';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LoginPage } from './components/LoginPage';
import { ProfileSection } from './components/ProfileSection';
import { WelcomePage, TripType } from './components/WelcomePage';
import { TripConfiguration, TripConfig } from './components/TripConfiguration';
import { PlaceDetailsModal } from './components/PlaceDetailsModal';
import {
  DirectionsRouteStopsBlock,
  DirectionsStartTimeRibbon,
  buildEtaByStopId,
} from './components/DirectionsRouteStopsBlock';
import { MapRouteOptionsChips } from './components/MapRouteOptionsChips';
import { getOrInitializeAccount, refreshAccountData, decrementResponseCount, canUseApp, getAccountData, updateAccountType, getEffectiveResponseCount } from './utils/accountUtils';
import { createSavedJourney, deleteSavedJourney } from './services/savedJourneysFirestore';
import { warmChatStopPhotosForSuggestions } from './utils/chatStopPhotoCache';

const WELCOME_MESSAGE = `👋 Welcome to RouteX!

I'm your AI travel assistant. To get started, please set your start and destination addresses using the location input bars at the top of the map.

Once you've entered both locations, I'll help you find the best route with interesting stops along the way.

You can also ask me:
• "Find coffee shops along my route"
• "I want to visit museums on my way"
• Or any other questions about your journey!`;

const LANGUAGE_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'English (US)', value: 'en-US' },
  { label: 'Chinese (Simplified)', value: 'zh-CN' },
  { label: 'Hindi', value: 'hi-IN' },
  { label: 'Spanish', value: 'es-ES' },
  { label: 'Arabic', value: 'ar-SA' },
  { label: 'French', value: 'fr-FR' },
  { label: 'Portuguese (Brazil)', value: 'pt-BR' },
  { label: 'Russian', value: 'ru-RU' },
  { label: 'German', value: 'de-DE' },
  { label: 'Japanese', value: 'ja-JP' },
  { label: 'Persian (Iran)', value: 'fa-IR' },
  { label: 'Turkish', value: 'tr-TR' },
  { label: 'Korean', value: 'ko-KR' },
  { label: 'Italian', value: 'it-IT' },
  { label: 'Dutch', value: 'nl-NL' },
  { label: 'Swedish', value: 'sv-SE' },
  { label: 'Danish', value: 'da-DK' },
  { label: 'Irish', value: 'ga-IE' },
  { label: 'Latin', value: 'la' },
];

// Main App Content (requires authentication)
const AppContent: React.FC = () => {
  const { user, loading, approvalStatus, signOut } = useAuth();
  const [selectedTripType, setSelectedTripType] = useState<TripType>(null);
  const [showPreTripSettings, setShowPreTripSettings] = useState(false);
  const [tripConfig, setTripConfig] = useState<TripConfig | null>(null);
  const [draftTripConfig, setDraftTripConfig] = useState<TripConfig | null>(null);
  const [preloadedJourney, setPreloadedJourney] = useState<SavedJourney | null>(null);
  const [showConfigEdit, setShowConfigEdit] = useState(false);
  const [isAddressPickingPhase, setIsAddressPickingPhase] = useState(false);
  const [isAddressPickingFromEdit, setIsAddressPickingFromEdit] = useState(false);
  const [preferredLanguage, setPreferredLanguage] = useState<string>(() => {
    try {
      const saved = window.localStorage.getItem('routex.preferredLanguage');
      if (saved && LANGUAGE_OPTIONS.some((o) => o.value === saved)) return saved;
    } catch {
      // ignore
    }
    const browserLang = navigator.language || 'en-US';
    const match = LANGUAGE_OPTIONS.find((o) => o.value === browserLang);
    return match ? match.value : 'en-US';
  });

  useEffect(() => {
    try {
      window.localStorage.setItem('routex.preferredLanguage', preferredLanguage);
    } catch {
      // ignore
    }
  }, [preferredLanguage]);

  // Premium-only language personalization: force non-premium users to English.
  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    const enforceLanguageByPlan = async () => {
      const account = await getOrInitializeAccount(user.uid);
      if (!cancelled && account.accountType !== 'Premium' && preferredLanguage !== 'en-US') {
        setPreferredLanguage('en-US');
      }
    };
    void enforceLanguageByPlan();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, preferredLanguage]);

  // Show loading screen while checking auth state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg animate-pulse">
            <span className="material-symbols-outlined text-white text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>
              route
            </span>
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!user) {
    return <LoginPage />;
  }

  // Show loading while we fetch approval status from Firestore
  if (approvalStatus === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg animate-pulse">
            <span className="material-symbols-outlined text-white text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>
              route
            </span>
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Show pending approval screen
  if (approvalStatus === 'pending') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-lg border border-gray-100 p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-md">
            <span className="material-symbols-outlined text-white text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>
              schedule
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">You're on the list!</h1>
          <p className="text-gray-500 mb-6 leading-relaxed">
            Your account is pending approval. We'll review your request and grant access shortly. No action needed on your end.
          </p>
          <p className="text-sm text-gray-400">Signed in as <span className="font-medium text-gray-600">{user.email}</span></p>
          <button
            className="mt-6 text-sm text-blue-500 hover:text-blue-600 underline"
            onClick={() => signOut()}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // Show welcome page if authenticated but no trip type selected
  if (!selectedTripType) {
    if (showPreTripSettings) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 pb-24">
          <div className="max-w-3xl mx-auto space-y-4">
            <button
              type="button"
              onClick={() => setShowPreTripSettings(false)}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              <span className="material-symbols-outlined text-[18px]">arrow_back</span>
              Back
            </button>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Settings</h2>
              <ProfileSection
                preferredLanguage={preferredLanguage}
                languageOptions={LANGUAGE_OPTIONS}
                onPreferredLanguageChange={setPreferredLanguage}
                onLoadSavedJourney={(journey) => {
                  const configFromJourney: TripConfig = {
                    travelMode: 'DRIVING',
                    radius: 5,
                    maxTime: 120,
                    numberOfPeople: 1,
                    startAddress: journey.startAddress,
                    endAddress: journey.endAddress,
                  };
                  setTripConfig(configFromJourney);
                  setDraftTripConfig(configFromJourney);
                  setPreloadedJourney(journey);
                  setShowPreTripSettings(false);
                  setSelectedTripType('single-day');
                }}
              />
            </div>
          </div>
        </div>
      );
    }
    return (
      <WelcomePage
        onSelectTripType={(tripType) => {
          // Only set trip type if it's not "coming soon"
          if (tripType === 'single-day') {
            setShowPreTripSettings(false);
            setSelectedTripType(tripType);
          }
        }}
        onOpenSettings={() => setShowPreTripSettings(true)}
      />
    );
  }

  if (selectedTripType === 'single-day' && isAddressPickingPhase) {
    return (
      <MainApp
        tripType={selectedTripType}
        tripConfig={draftTripConfig || tripConfig}
        onBackToWelcome={() => {
          setSelectedTripType(null);
          setTripConfig(null);
          setDraftTripConfig(null);
          setIsAddressPickingPhase(false);
          setIsAddressPickingFromEdit(false);
        }}
        onEditConfig={() => setShowConfigEdit(true)}
        preferredLanguage={preferredLanguage}
        chatEnabled={false}
        forceReturnToConfigAfterAddressCapture
        onAddressCaptureComplete={(startAddress, endAddress) => {
          setDraftTripConfig((prev) => ({
            ...(prev || {
              travelMode: 'DRIVING',
              radius: 5,
              maxTime: 120,
              numberOfPeople: 1,
            }),
            startAddress,
            endAddress,
          }));
          setIsAddressPickingPhase(false);
          if (isAddressPickingFromEdit) {
            setShowConfigEdit(true);
          }
          setIsAddressPickingFromEdit(false);
        }}
      />
    );
  }

  // Show trip configuration page if single-day is selected but config not completed
  if (selectedTripType === 'single-day' && !tripConfig) {
    return (
      <TripConfiguration
        onComplete={(config) => {
          setTripConfig(config);
          setDraftTripConfig(config);
        }}
        onBack={() => setSelectedTripType(null)}
        initialConfig={draftTripConfig || undefined}
        onSetAddressesOnMap={(configDraft) => {
          setDraftTripConfig({
            ...configDraft,
            startAddress: '',
            endAddress: '',
          });
          setIsAddressPickingPhase(true);
          setIsAddressPickingFromEdit(false);
        }}
      />
    );
  }

  return (
    <div className="relative">
      <MainApp
        tripType={selectedTripType}
        tripConfig={tripConfig}
        preloadedJourney={preloadedJourney}
        onBackToWelcome={() => {
          setSelectedTripType(null);
          setTripConfig(null);
          setDraftTripConfig(null);
          setPreloadedJourney(null);
          setIsAddressPickingPhase(false);
          setIsAddressPickingFromEdit(false);
        }}
        onEditConfig={() => {
          setDraftTripConfig(tripConfig);
          setShowConfigEdit(true);
        }}
        preferredLanguage={preferredLanguage}
        onPreferredLanguageChange={setPreferredLanguage}
      />
      {showConfigEdit && selectedTripType === 'single-day' && (
        <div className="fixed inset-0 z-[80]">
          <TripConfiguration
            onComplete={(config) => {
              setTripConfig(config);
              setDraftTripConfig(config);
              setShowConfigEdit(false);
            }}
            onBack={() => {
              setShowConfigEdit(false);
              setDraftTripConfig(tripConfig);
            }}
            initialConfig={draftTripConfig || tripConfig || undefined}
            onSetAddressesOnMap={(configDraft) => {
              setDraftTripConfig({
                ...configDraft,
                startAddress: '',
                endAddress: '',
              });
              setShowConfigEdit(false);
              setIsAddressPickingPhase(true);
              setIsAddressPickingFromEdit(true);
            }}
          />
        </div>
      )}
    </div>
  );
};

// Main App Component
interface MainAppProps {
  tripType: TripType;
  tripConfig: TripConfig | null;
  preloadedJourney?: SavedJourney | null;
  onBackToWelcome: () => void;
  onEditConfig: () => void;
  preferredLanguage: string;
  onPreferredLanguageChange?: (lang: string) => void;
  chatEnabled?: boolean;
  initialActiveTab?: string;
  forceReturnToConfigAfterAddressCapture?: boolean;
  onAddressCaptureComplete?: (startAddress: string, endAddress: string) => void;
}

const MainApp: React.FC<MainAppProps> = ({
  tripType,
  tripConfig,
  preloadedJourney = null,
  onBackToWelcome,
  onEditConfig,
  preferredLanguage,
  onPreferredLanguageChange,
  chatEnabled = true,
  initialActiveTab = 'map',
  forceReturnToConfigAfterAddressCapture = false,
  onAddressCaptureComplete,
}) => {
  // tripType will be used later to customize behavior based on trip type
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRouteLoading, setIsRouteLoading] = useState(false);
  const [loadingStatusIndex, setLoadingStatusIndex] = useState(0);
  const LOADING_STATUS_MESSAGES = ['Thinking...', 'Searching...', 'Analysing...', 'Routing...'];
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [location, setLocation] = useState<Coordinates | undefined>(undefined);
  const [activeTab, setActiveTab] = useState(initialActiveTab); // Default to map tab now
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [sessionId] = useState(() => getOrCreateSessionId());
  const [startLocation, setStartLocation] = useState(tripConfig?.startAddress || '');
  const [endLocation, setEndLocation] = useState(tripConfig?.endAddress || '');
  const [startLocationCoords, setStartLocationCoords] = useState<Location | undefined>(undefined);
  const [endLocationCoords, setEndLocationCoords] = useState<Location | undefined>(undefined);
  const [isSelectingLocation, setIsSelectingLocation] = useState<'start' | 'end' | null>(null);
  const [suggestedPlaces, setSuggestedPlaces] = useState<Place[]>([]);
  const [rankedPlaces, setRankedPlaces] = useState<Place[]>([]);
  const [mapCenter, setMapCenter] = useState<Location | undefined>(undefined);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
  const [isPlaceDetailsOpen, setIsPlaceDetailsOpen] = useState(false);
  const [chatButtonPosition, setChatButtonPosition] = useState({ x: 16, y: 96 }); // Default: left-4 bottom-24 (16px from left, 96px from bottom)
  const [isDraggingChatButton, setIsDraggingChatButton] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [routePolyline, setRoutePolyline] = useState<string | undefined>(undefined);
  const [correctedRoutePolyline, setCorrectedRoutePolyline] = useState<string | undefined>(undefined);
  const [routeSuggestions, setRouteSuggestions] = useState<RouteSuggestion[]>([]);
  const [fastestRoute, setFastestRoute] = useState<RouteSuggestion | null>(null); // Store fastest route for comparison
  const [activeRouteSuggestion, setActiveRouteSuggestion] = useState<number>(0);
  const [showGoConfirmDialog, setShowGoConfirmDialog] = useState(false);
  const [showResetSessionConfirmDialog, setShowResetSessionConfirmDialog] = useState(false);
  const [showBookingPlaceholder, setShowBookingPlaceholder] = useState(false);
  const [showNavigationAppDialog, setShowNavigationAppDialog] = useState(false);
  const [appleLegPoints, setAppleLegPoints] = useState<string[]>([]);
  const [currentAppleLegIndex, setCurrentAppleLegIndex] = useState<number>(0); // leg i => points[i] -> points[i+1]
  const [savedJourneyMessage, setSavedJourneyMessage] = useState<string | null>(null);
  const [savedJourneyMessageTone, setSavedJourneyMessageTone] = useState<'success' | 'error'>('success');
  const [savedJourneyIdForCurrentRoute, setSavedJourneyIdForCurrentRoute] = useState<string | null>(null);
  const [savedJourneySignature, setSavedJourneySignature] = useState<string>('');
  const [canUseAppNow, setCanUseAppNow] = useState(true);
  const [openingHoursByPlaceId, setOpeningHoursByPlaceId] = useState<Record<string, OpeningHours | null>>({});
  const [directionsStartTimeMode, setDirectionsStartTimeMode] = useState<'now' | 'custom'>('now');
  const [directionsStartDateTime, setDirectionsStartDateTime] = useState<string>(() => {
    try {
      return window.localStorage.getItem('routex.directionsStartDateTime') || '';
    } catch {
      return '';
    }
  }); // from <input type="datetime-local">

  useEffect(() => {
    if (tripConfig?.startAddress) setStartLocation(tripConfig.startAddress);
    if (tripConfig?.endAddress) setEndLocation(tripConfig.endAddress);
  }, [tripConfig?.startAddress, tripConfig?.endAddress]);
  const hasReturnedToConfigAfterAddressCaptureRef = useRef(false);
  useEffect(() => {
    if (!forceReturnToConfigAfterAddressCapture || hasReturnedToConfigAfterAddressCaptureRef.current) return;
    const s = startLocation.trim();
    const e = endLocation.trim();
    // Wait until both addresses are finalized via autocomplete/select (coords resolved),
    // not just typed text.
    if (!s || !e || !startLocationCoords || !endLocationCoords) return;
    hasReturnedToConfigAfterAddressCaptureRef.current = true;
    onAddressCaptureComplete?.(s, e);
  }, [
    forceReturnToConfigAfterAddressCapture,
    startLocation,
    endLocation,
    startLocationCoords,
    endLocationCoords,
    onAddressCaptureComplete,
  ]);
  const [stayOverridesByStopId, setStayOverridesByStopId] = useState<Record<string, number>>(() => {
    try {
      const raw = window.localStorage.getItem('routex.stayOverridesByStopId');
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return {};
      const out: Record<string, number> = {};
      for (const [k, v] of Object.entries(parsed)) {
        const n = Number(v);
        if (k && Number.isFinite(n) && n >= 0) out[k] = n;
      }
      return out;
    } catch {
      return {};
    }
  });
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const requestedOpeningHoursRef = useRef<Set<string>>(new Set());
  const activeRouteSuggestionRef = useRef<number>(0);
  const reorderedStopsRef = useRef<Place[] | null>(null);
  const hasAppliedPreloadedJourneyRef = useRef(false);
  const lastAutoLocalizedRouteKeyRef = useRef<string>('');

  // Persist Directions booking inputs
  useEffect(() => {
    try {
      window.localStorage.setItem('routex.directionsStartTimeMode', directionsStartTimeMode);
      window.localStorage.setItem('routex.directionsStartDateTime', directionsStartDateTime);
    } catch {
      // ignore
    }
  }, [directionsStartTimeMode, directionsStartDateTime]);

  // Persist per-stop stay overrides
  useEffect(() => {
    try {
      window.localStorage.setItem(
        'routex.stayOverridesByStopId',
        JSON.stringify(stayOverridesByStopId)
      );
    } catch {
      // ignore
    }
  }, [stayOverridesByStopId]);

  // Sensors for @dnd-kit (Directions tab stop reordering)
  // Pointer + touch: scrollable route-details panel needs TouchSensor (delay) so vertical scroll
  // doesn’t eat drags; same behavior as the old Directions tab, but inside overflow-y-auto.
  const directionsSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Keep ref in sync with state
  useEffect(() => {
    activeRouteSuggestionRef.current = activeRouteSuggestion;
  }, [activeRouteSuggestion]);

  // Correct/refresh route polyline on frontend using Google Directions to avoid occasional mismatches.
  // We derive the polyline from Google's returned route geometry (overview_path/legs),
  // so the yellow line always follows start -> ordered stops -> destination.
  useEffect(() => {
    let cancelled = false;

    const extractEncodedPolylineFromRoute = (route: any): string => {
      // Preferred source in JS SDK: overview_path (array of LatLng)
      const overviewPath = route?.overview_path;
      if (
        overviewPath &&
        Array.isArray(overviewPath) &&
        overviewPath.length > 1 &&
        window.google?.maps?.geometry?.encoding
      ) {
        try {
          return window.google.maps.geometry.encoding.encodePath(overviewPath);
        } catch {
          // fall through
        }
      }

      // Secondary source: overview_polyline object variants
      const overviewPolyline = route?.overview_polyline;
      const encodedFromOverview =
        overviewPolyline?.encoded_path ||
        overviewPolyline?.encodedPath ||
        overviewPolyline?.points ||
        '';
      if (encodedFromOverview) {
        return encodedFromOverview;
      }

      // Fallback: stitch path from legs/steps
      if (route?.legs && window.google?.maps?.geometry?.encoding) {
        try {
          const stitchedPath: google.maps.LatLng[] = [];
          route.legs.forEach((leg: any) => {
            (leg?.steps || []).forEach((step: any) => {
              if (step?.path && Array.isArray(step.path) && step.path.length > 0) {
                stitchedPath.push(...step.path);
              }
            });
          });
          if (stitchedPath.length > 1) {
            return window.google.maps.geometry.encoding.encodePath(stitchedPath);
          }
        } catch {
          // ignore
        }
      }

      return '';
    };

    const syncCorrectRoutePolyline = async () => {
      if (!startLocationCoords || !endLocationCoords) {
        setCorrectedRoutePolyline(undefined);
        return;
      }
      if (!window.google?.maps?.DirectionsService || !window.google?.maps?.TravelMode) {
        setCorrectedRoutePolyline(undefined);
        return;
      }

      try {
        // Clear stale corrected polyline so we don't keep showing an old route.
        setCorrectedRoutePolyline(undefined);

        const directionsService = new window.google.maps.DirectionsService();
        const mode = tripConfig?.travelMode || 'DRIVING';
        const waypoints = suggestedPlaces.map((p) => ({
          location: new window.google.maps.LatLng(p.location.lat, p.location.lng),
          stopover: true,
        }));

        directionsService.route(
          {
            origin: new window.google.maps.LatLng(
              startLocationCoords.lat,
              startLocationCoords.lng
            ),
            destination: new window.google.maps.LatLng(
              endLocationCoords.lat,
              endLocationCoords.lng
            ),
            waypoints,
            optimizeWaypoints: false,
            travelMode: window.google.maps.TravelMode[mode],
          },
          (result, status) => {
            if (cancelled) return;
            if (status === window.google.maps.DirectionsStatus.OK && result?.routes?.[0]) {
              const encoded = extractEncodedPolylineFromRoute(result.routes[0]);
              if (encoded) {
                setCorrectedRoutePolyline(encoded);
                return;
              }
            }
            // Leave undefined if correction fails (prefer no line over wrong line).
            setCorrectedRoutePolyline(undefined);
          }
        );
      } catch {
        setCorrectedRoutePolyline(undefined);
      }
    };

    syncCorrectRoutePolyline();
    return () => {
      cancelled = true;
    };
  }, [startLocationCoords, endLocationCoords, suggestedPlaces, tripConfig?.travelMode]);

  useEffect(() => {
    selectedFiltersRef.current = selectedFilters;
  }, [selectedFilters]);

  // Fetch opening hours for places shown in map route details (Google Places API)
  useEffect(() => {
    if (activeTab !== 'map' || !suggestedPlaces.length || !window.google?.maps?.places) return;
    if (!placesServiceRef.current) {
      const mapDiv = document.createElement('div');
      const map = new window.google.maps.Map(mapDiv);
      placesServiceRef.current = new window.google.maps.places.PlacesService(map);
    }
    const service = placesServiceRef.current;
    suggestedPlaces.forEach((place) => {
      if (!place.place_id || place.place_id.startsWith('stop-')) return;
      if (place.place_id in openingHoursByPlaceId || requestedOpeningHoursRef.current.has(place.place_id)) return;
      requestedOpeningHoursRef.current.add(place.place_id);
      service.getDetails(
        { placeId: place.place_id, fields: ['opening_hours'] },
        (result: google.maps.places.PlaceResult | null, status: google.maps.places.PlacesServiceStatus) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !result) {
            setOpeningHoursByPlaceId((prev) => ({ ...prev, [place.place_id]: null }));
            return;
          }
          const oh = result.opening_hours;
          if (!oh) {
            setOpeningHoursByPlaceId((prev) => ({ ...prev, [place.place_id]: null }));
            return;
          }
          setOpeningHoursByPlaceId((prev) => ({
            ...prev,
            [place.place_id]: { open_now: oh.open_now, weekday_text: oh.weekday_text ?? undefined },
          }));
        }
      );
    });
  }, [activeTab, suggestedPlaces, openingHoursByPlaceId]);

  const [isChatExpanded, setIsChatExpanded] = useState(false);
  const [chatSwipeStartY, setChatSwipeStartY] = useState<number>(0);
  const [isLocationPanelCollapsed, setIsLocationPanelCollapsed] = useState(false);
  const [isDirectionsCalendarOpen, setIsDirectionsCalendarOpen] = useState(false);

  const selectedFiltersRef = useRef<string[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatMessagesContainerRef = useRef<HTMLDivElement>(null);
  /** When reopening chat, restore this scroll offset (null = scroll to bottom instead). */
  const savedChatScrollTopRef = useRef<number | null>(null);
  /** Skip auto scroll-to-bottom while applying a saved scroll position on open. */
  const isRestoringChatScrollRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const endLocationInputRef = useRef<HTMLInputElement>(null);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const chatButtonRef = useRef<HTMLButtonElement>(null);
  const isDraggingChatButtonRef = useRef(false);

  // Voice input and TTS controls
  const [isMuted, setIsMuted] = useState(false);
  const isMutedRef = useRef(false); // Ref to track mute state for event listeners
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const isButtonHeldRef = useRef<boolean>(false); // Track if voice button is being held down
  const accumulatedTranscriptRef = useRef<string>(''); // Accumulate final transcript across continuous recognition events
  const isMessageSentRef = useRef<boolean>(false); // Track if a message was just sent to prevent onresult from restoring text
  const lastSpokenMessageIdRef = useRef<string>(''); // Track last spoken message ID
  const ttsGenerationAbortRef = useRef<AbortController | null>(null); // Track TTS generation to cancel if needed
  const isGeneratingTTSRef = useRef<boolean>(false); // Track if TTS is currently being generated

  // Ensure map tab is active when MainApp mounts (especially after configuration)
  useEffect(() => {
    setActiveTab('map');
  }, []);

  // Directions tab was removed; coerce any stale `route` selection to map
  useEffect(() => {
    if (activeTab === 'route') setActiveTab('map');
  }, [activeTab]);

  // Check account status periodically
  useEffect(() => {
    if (!user?.uid) return;

    const checkAccountStatus = async () => {
      await refreshAccountData(user.uid!);
      const accountData = getAccountData(user.uid!);
      const canUse = canUseApp(user.uid!);
      setCanUseAppNow(canUse);
      if (accountData && accountData.accountType === 'Free' && getEffectiveResponseCount(user.uid!) === 0) {
        setShowUpgradePrompt(true);
      }
    };

    void checkAccountStatus();
    const interval = setInterval(() => {
      void checkAccountStatus();
    }, 3000);

    return () => clearInterval(interval);
  }, [user?.uid]);

  // Sync mute ref with state
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  // Get user's location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          // Update map center with user's location
          setMapCenter({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.warn("Location access denied:", error);
        }
      );
    }
  }, []);

  // After config -> first fastest-route visualization, auto-run one localise action.
  // This mirrors a manual "Localise me" tap and helps avoid extra zooming.
  useEffect(() => {
    const start = (tripConfig?.startAddress || '').trim();
    const end = (tripConfig?.endAddress || '').trim();
    const fastestPolyline = fastestRoute?.polyline || '';
    if (!start || !end || !fastestPolyline) return;
    if (start.toLowerCase() !== end.toLowerCase()) return;
    if (activeTab !== 'map') return;
    if (!location) return;

    const routeKey = `${start}|${end}|${fastestPolyline}`;
    if (lastAutoLocalizedRouteKeyRef.current === routeKey) return;

    const timer = setTimeout(() => {
      setMapCenter({ lat: location.latitude, lng: location.longitude });
      lastAutoLocalizedRouteKeyRef.current = routeKey;
    }, 350);

    return () => clearTimeout(timer);
  }, [tripConfig?.startAddress, tripConfig?.endAddress, fastestRoute?.polyline, activeTab, location]);

  const scrollToBottom = useCallback(() => {
    const el = chatMessagesContainerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const closeChatSavingScroll = useCallback(() => {
    const el = chatMessagesContainerRef.current;
    if (el) {
      savedChatScrollTopRef.current = el.scrollTop;
    }
    setIsChatOpen(false);
  }, []);

  // When chat opens: restore scroll from last close, or jump to bottom
  useEffect(() => {
    if (!isChatOpen) return;
    const saved = savedChatScrollTopRef.current;
    if (saved !== null) {
      savedChatScrollTopRef.current = null;
      isRestoringChatScrollRef.current = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = chatMessagesContainerRef.current;
          if (el) el.scrollTop = saved;
          isRestoringChatScrollRef.current = false;
        });
      });
      return;
    }
    scrollToBottom();
  }, [isChatOpen, scrollToBottom]);

  // Scroll to bottom when messages change while chat is open
  useEffect(() => {
    if (!isChatOpen) return;
    if (isRestoringChatScrollRef.current) return;
    scrollToBottom();
  }, [messages, isChatOpen, scrollToBottom]);

  // Scroll to bottom when composing (so user can read what they're typing)
  useEffect(() => {
    if (!isChatOpen || !inputText.trim()) return;
    if (isRestoringChatScrollRef.current) return;
    scrollToBottom();
  }, [inputText, scrollToBottom, isChatOpen]);

  // Cycle loading status: Thinking → Searching → Analysing → Routing, then stay at Routing
  useEffect(() => {
    if (!isLoading) {
      setLoadingStatusIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setLoadingStatusIndex((i) => (i >= 3 ? 3 : i + 1));
    }, 4000);
    return () => clearInterval(interval);
  }, [isLoading]);

  // Focus input when chat opens
  useEffect(() => {
    if (isChatOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isChatOpen]);

  // Helper function to speak assistant messages using TTS
  const speakMessage = useCallback((text: string, language?: string, abortSignal?: AbortSignal) => {
    // Don't speak if muted - check at the very start using ref for immediate check
    if (isMutedRef.current || isMuted) {
      return Promise.resolve();
    }

    const lang = language || preferredLanguage || navigator.language || 'en-US';

    // Stop any currently playing audio IMMEDIATELY
    if (currentAudioRef.current) {
      try {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
        currentAudioRef.current = null;
      } catch (_e) {
        // Ignore errors when stopping
      }
    }

    // Cancel any pending TTS generation (only if no abortSignal - caller manages cancellation when signal provided)
    if (!abortSignal && ttsGenerationAbortRef.current) {
      ttsGenerationAbortRef.current.abort();
      ttsGenerationAbortRef.current = null;
    }

    // Mark that we're generating TTS
    isGeneratingTTSRef.current = true;

    // Use provided abort signal or create new abort controller
    let signalToUse: AbortSignal;
    let abortController: AbortController | null = null;

    if (abortSignal) {
      // Use the provided signal directly - caller (useEffect) manages the controller
      signalToUse = abortSignal;
    } else {
      // Create new abort controller
      abortController = new AbortController();
      signalToUse = abortController.signal;
      ttsGenerationAbortRef.current = abortController;
    }

    // Generate and play TTS with abort signal support
    return textToSpeech(text, lang, signalToUse)
      .then(audioBlob => {
        // Check if this generation was aborted
        if (signalToUse.aborted) {
          // Clean up the blob URL
          const tempUrl = URL.createObjectURL(audioBlob);
          URL.revokeObjectURL(tempUrl);
          isGeneratingTTSRef.current = false;
          return;
        }

        // Check mute state again before playing (user might have muted while loading)
        if (isMutedRef.current || isMuted) {
          URL.revokeObjectURL(URL.createObjectURL(audioBlob));
          isGeneratingTTSRef.current = false;
          return;
        }

        // Stop any audio that might have started playing while we were generating
        if (currentAudioRef.current) {
          try {
            currentAudioRef.current.pause();
            currentAudioRef.current.currentTime = 0;
          } catch (e) {
            // Ignore errors
          }
        }

        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        currentAudioRef.current = audio;

        // Play audio
        audio.play().catch(error => {
          console.error('Error playing TTS audio:', error);
          currentAudioRef.current = null;
          URL.revokeObjectURL(audioUrl);
          isGeneratingTTSRef.current = false;
        });

        // Clean up the URL after playing
        audio.addEventListener('ended', () => {
          URL.revokeObjectURL(audioUrl);
          currentAudioRef.current = null;
          isGeneratingTTSRef.current = false;
        }, { once: true });

        // Clean up on error
        audio.addEventListener('error', () => {
          URL.revokeObjectURL(audioUrl);
          currentAudioRef.current = null;
          isGeneratingTTSRef.current = false;
        }, { once: true });
      })
      .catch(error => {
        // Don't log if aborted (this is expected behavior)
        if (error.name === 'AbortError' || signalToUse.aborted) {
          // This is expected - the request was cancelled
          isGeneratingTTSRef.current = false;
          return;
        }
        // Log other errors
        console.error('Error generating TTS:', error);
        isGeneratingTTSRef.current = false;
      })
      .finally(() => {
        // Clear abort controller if this was the current one (and we created it)
        if (abortController && ttsGenerationAbortRef.current === abortController) {
          ttsGenerationAbortRef.current = null;
        }
      });
  }, [isMuted, preferredLanguage]);

  // Auto-speak only the last assistant message (force stop any previous playback)
  useEffect(() => {
    if (messages.length === 0) return;
    const lastAssistantMessage = [...messages].reverse().find(msg => msg.role === Role.ASSISTANT);
    if (
      lastAssistantMessage &&
      lastAssistantMessage.id !== lastSpokenMessageIdRef.current &&
      !isMuted
    ) {
      // Update the last spoken ID immediately to prevent duplicate calls
      lastSpokenMessageIdRef.current = lastAssistantMessage.id;

      // Force stop any currently playing audio IMMEDIATELY
      if (currentAudioRef.current) {
        try {
          currentAudioRef.current.pause();
          currentAudioRef.current.currentTime = 0;
          currentAudioRef.current = null;
        } catch (_e) {
          // Ignore errors when stopping
        }
      }

      // Cancel any pending TTS generation
      if (ttsGenerationAbortRef.current) {
        ttsGenerationAbortRef.current.abort();
        ttsGenerationAbortRef.current = null;
      }
      isGeneratingTTSRef.current = false;

      // Clean the text (remove emojis, etc.) and speak
      const cleanText = lastAssistantMessage.text.replace(/🗺️/g, '').trim();
      if (cleanText) {
        // Create abort controller for this specific TTS call
        const abortController = new AbortController();
        ttsGenerationAbortRef.current = abortController;

        // Call speakMessage with the abort signal
        speakMessage(cleanText, preferredLanguage || navigator.language || 'en-US', abortController.signal).catch(err => {
          // Ignore abort errors
          if (err.name !== 'AbortError') {
            console.error('TTS error:', err);
          }
        });
      }
    }
  }, [messages, isMuted, speakMessage]);

  // Toggle mute state
  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const newMuted = !prev;
      isMutedRef.current = newMuted; // Update ref immediately

      // Stop any currently playing audio when muting
      if (newMuted) {
        // Muting - stop any playing audio immediately
        if (currentAudioRef.current) {
          currentAudioRef.current.pause();
          currentAudioRef.current.currentTime = 0;
          currentAudioRef.current = null;
        }
      }
      // If unmuting, audio will play on next speakMessage call
      return newMuted;
    });
  }, []);

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = true; // Keep listening continuously, don't stop on silence
        recognition.interimResults = true;
        recognition.lang = preferredLanguage || navigator.language || 'en-US';

        recognition.onstart = () => {
          setIsListening(true);
        };

        recognition.onend = () => {
          // Only stop if button is released, otherwise restart recognition
          if (isButtonHeldRef.current) {
            // Button is still held, restart recognition to continue listening
            try {
              recognition.start();
            } catch (error) {
              // Recognition might already be starting, ignore error
              // Recognition might already be starting
            }
          } else {
            // Button was released, stop listening
            setIsListening(false);
          }
        };

        recognition.onerror = (event: any) => {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);
          isButtonHeldRef.current = false; // Reset button held state on error
          if (event.error === 'not-allowed') {
            alert('Microphone access denied. Please enable microphone permissions.');
          }
        };

        recognition.onresult = (event: any) => {
          // Don't update input if a message was just sent (prevents text from reappearing)
          if (isMessageSentRef.current) {
            return;
          }

          let finalTranscript = '';
          let interimTranscript = '';

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
            } else {
              interimTranscript += transcript;
            }
          }

          // Accumulate final transcripts across continuous recognition events
          if (finalTranscript.trim()) {
            accumulatedTranscriptRef.current += finalTranscript;
          }

          // Show accumulated final text + current interim results
          const displayText = accumulatedTranscriptRef.current.trim() + (interimTranscript ? ' ' + interimTranscript : '');
          if (displayText.trim()) {
            setInputText(displayText.trim());
          }
        };

        recognitionRef.current = recognition;
      }
    }
  }, [preferredLanguage]);

  // Start voice input (green = idle, red = recording)
  const startVoiceInput = useCallback(() => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in this browser.');
      return;
    }

    // Stop agent TTS immediately so the user can talk (same as muting)
    if (currentAudioRef.current) {
      try {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
        currentAudioRef.current = null;
      } catch (e) {
        // Ignore errors when stopping
      }
    }
    if (ttsGenerationAbortRef.current) {
      ttsGenerationAbortRef.current.abort();
      ttsGenerationAbortRef.current = null;
    }
    isGeneratingTTSRef.current = false;

    try {
      isButtonHeldRef.current = true; // Mark button as held
      accumulatedTranscriptRef.current = ''; // Reset accumulated transcript for new recording
      setIsListening(true); // Turn mic red immediately so user sees recording state
      setInputText('');
      recognitionRef.current.start();
    } catch (error) {
      console.error('Error starting speech recognition:', error);
      setIsListening(false);
      isButtonHeldRef.current = false;
    }
  }, []);

  // Stop voice input (release mic → stop recording, text stays in input)
  const stopVoiceInput = useCallback(() => {
    isButtonHeldRef.current = false; // Mark button as released
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        console.error('Error stopping speech recognition:', error);
      }
      setIsListening(false); // Turn mic green again

      // Ensure the final transcript is set in inputText
      if (accumulatedTranscriptRef.current.trim()) {
        setInputText(accumulatedTranscriptRef.current.trim());
      }

      // Focus the input and position cursor at the end of the text after a short delay
      setTimeout(() => {
        if (inputRef.current && isChatOpen) {
          inputRef.current.focus();
          // Position cursor at the end of the text
          const textLength = inputRef.current.value.length;
          inputRef.current.setSelectionRange(textLength, textLength);
        }
      }, 100);
    }
  }, [isChatOpen]);

  // Add welcome message when chat opens if no start/end locations are set.
  // Keep logic consistent with other assistant messages: author in EN, translate for UI/TTS.
  useEffect(() => {
    if (!(isChatOpen && messages.length === 0 && !startLocation && !endLocation)) return;

    let cancelled = false;
    const addWelcomeMessage = async () => {
      let welcomeToShow = WELCOME_MESSAGE;
      const isPreferredEnglish = (preferredLanguage || '').toLowerCase().startsWith('en');
      if (!isPreferredEnglish) {
        try {
          const translated = await translateText(WELCOME_MESSAGE, 'en', preferredLanguage);
          if (translated?.translated_text) welcomeToShow = translated.translated_text;
        } catch (e) {
          console.warn('EN->preferred translation failed for welcome msg:', e);
        }
      }
      if (cancelled) return;
      setMessages([
        {
          id: 'welcome',
          role: Role.ASSISTANT,
          text: welcomeToShow,
          timestamp: Date.now()
        }
      ]);
      // TTS will be handled by the centralized useEffect watching messages
    };

    void addWelcomeMessage();
    return () => {
      cancelled = true;
    };
  }, [isChatOpen, startLocation, endLocation, messages.length, preferredLanguage]);


  // Helper function to get fastest route for comparison (simple route without stops)
  const getFastestRoute = useCallback(async (start: string, end: string) => {
    if (!start || !end) return null;

    try {
      const travelMode = tripConfig?.travelMode || 'DRIVING';
      const fastestRouteResponse = await getRoute({
        start,
        end,
        mode: travelMode,
        preferences: '', // No preferences = fastest route
        alternative: false
      });

      if (fastestRouteResponse.polyline && fastestRouteResponse.legs) {
        const totalTimeMinutes = fastestRouteResponse.legs.reduce(
          (sum, leg) => sum + (leg.duration?.value || 0),
          0
        ) / 60;

        const fastestRouteSuggestion: RouteSuggestion = {
          name: 'Fastest Route',
          stops: [],
          suggested_stops: [],
          num_stops: 0,
          total_time: totalTimeMinutes,
          polyline: fastestRouteResponse.polyline,
          legs: fastestRouteResponse.legs
        };
        return fastestRouteSuggestion;
      }
    } catch (error) {
      console.error('Failed to fetch fastest route:', error);
    }
    return null;
  }, [tripConfig, sessionId]);


  const lastRoutePipelineKeyRef = useRef<string>('');
  const suppressNextAutoPipelineRef = useRef(false);

  const runRoutePipeline = useCallback(async (newStartRaw: string, newEndRaw: string) => {
    if (!chatEnabled) return;
    const newStart = (newStartRaw || '').trim();
    const newEnd = (newEndRaw || '').trim();
    if (!newStart || !newEnd) return;

    const routeKey = `${newStart}|${newEnd}`;
    if (lastRoutePipelineKeyRef.current === routeKey) {
      // Already ran for this pair; do not force-open chat again
      return;
    }
    lastRoutePipelineKeyRef.current = routeKey;

    // Open chat immediately and show a short "agent is coming up" placeholder.
    setIsChatOpen(true);
    const preparingId = `assistant-preparing-${routeKey}`;
    setMessages((prev) => {
      if (prev.some((m) => m.id === preparingId)) return prev;
      return [
        ...prev.filter((m) => m.id !== 'welcome'),
        {
          id: preparingId,
          role: Role.ASSISTANT,
          text: '…',
          timestamp: Date.now(),
        },
      ];
    });

    // Remove welcome message if it exists when locations are set
    setMessages((prev) => prev.filter((msg) => msg.id !== 'welcome'));

    // Fetch fastest route (previous pipeline behavior)
    setIsRouteLoading(true);
    try {
      const travelMode = tripConfig?.travelMode || 'DRIVING';
      const fastestRouteResponse = await getRoute({
        start: newStart,
        end: newEnd,
        mode: travelMode,
        preferences: '',
        alternative: false,
      });

      if (fastestRouteResponse.polyline && fastestRouteResponse.legs) {
        const totalTimeMinutes =
          fastestRouteResponse.legs.reduce((sum, leg) => sum + (leg.duration?.value || 0), 0) / 60;

        const fastestRouteSuggestion: RouteSuggestion = {
          name: 'Fastest Route',
          stops: [],
          suggested_stops: [],
          num_stops: 0,
          total_time: totalTimeMinutes,
          polyline: fastestRouteResponse.polyline,
          legs: fastestRouteResponse.legs,
        };

        setFastestRoute(fastestRouteSuggestion);
        setRoutePolyline(fastestRouteResponse.polyline);
        setRouteSuggestions([]);
        setActiveRouteSuggestion(-1);
        setSuggestedPlaces([]);

        // Set start/end coords from legs so Go!/Start Journey work
        if (fastestRouteResponse.legs?.length) {
          const firstLeg = fastestRouteResponse.legs[0];
          const lastLeg = fastestRouteResponse.legs[fastestRouteResponse.legs.length - 1];
          if (firstLeg?.start_location) setStartLocationCoords(firstLeg.start_location);
          if (lastLeg?.end_location) setEndLocationCoords(lastLeg.end_location);
        }
      }
    } catch (error) {
      console.error('Failed to fetch fastest route:', error);
    } finally {
      setIsRouteLoading(false);
    }

    // Greeting (deduped) – always authored in English, then translated for UI/TTS if needed.
    const greetingId = `route-greeting-${routeKey}`;
    const englishGreeting = `👋 Hi! I'm your RouteX assistant. I'm happy to help you plan your trip from ${newStart} to ${newEnd}. How can I assist you today?\n\nTalk with me or type in the chat.\n\nPlease share as many travel details as you can (must-see stops, food preferences, budget, pace, accessibility needs, or anything else on your mind) so I can tailor better suggestions.\n\nFor your safety, please do not share sensitive personal information such as passwords, bank details, or ID numbers.`;
    let greetingToShow = englishGreeting;
    const isPreferredEnglish = (preferredLanguage || '').toLowerCase().startsWith('en');
    if (!isPreferredEnglish) {
      try {
        const translated = await translateText(englishGreeting, 'en', preferredLanguage);
        if (translated?.translated_text) greetingToShow = translated.translated_text;
      } catch (e) {
        console.warn('EN->preferred translation failed for greeting:', e);
      }
    }
    setMessages((prev) => {
      if (prev.some((m) => m.id === greetingId)) return prev;
      return [
        ...prev.filter((m) => m.id !== preparingId),
        {
          id: greetingId,
          role: Role.ASSISTANT,
          text: greetingToShow,
          timestamp: Date.now(),
        },
      ];
    });
  }, [tripConfig?.travelMode, preferredLanguage, chatEnabled]);

  // Handle location selection from map click or input
  const handleLocationSelect = useCallback(async (
    location: Location,
    address: string,
    type: 'start' | 'end'
  ) => {
    // Don't update addresses while chat open, but allow pipeline to run.
    // (Inputs are already disabled when chat is open.)

    setIsSelectingLocation(null);

    // Update state
    let newStart = startLocation;
    let newEnd = endLocation;

    if (type === 'start') {
      setStartLocation(address);
      setStartLocationCoords(location);
      newStart = address;

      // Automatically focus on end location input after start is selected
      setTimeout(() => {
        if (endLocationInputRef.current && !isChatOpen) {
          endLocationInputRef.current.focus();
          setIsSelectingLocation('end');
        }
      }, 100);
    } else {
      setEndLocation(address);
      setEndLocationCoords(location);
      newEnd = address;
    }

    // Update map center to selected location
    setMapCenter(location);

    // Run the previous pipeline as soon as both are provided (selection path)
    if (chatEnabled && newStart && newEnd) {
      await runRoutePipeline(newStart, newEnd);
    }
  }, [startLocation, endLocation, runRoutePipeline, chatEnabled]);

  // Handle location input change (text input)
  const handleLocationInputChange = useCallback((value: string, type: 'start' | 'end') => {
    // Prevent changes when chat is open
    if (isChatOpen) {
      return;
    }
    if (type === 'start') {
      setStartLocation(value);
    } else {
      setEndLocation(value);
    }
    // Don't auto-plan route - user must click the "Plan Route" button
  }, [isChatOpen]);

  // Typed addresses path: when both start & end are present, run the same pipeline (deduped).
  useEffect(() => {
    if (!chatEnabled) return;
    if (isChatOpen) return;
    if (isSelectingLocation) return;
    if (suppressNextAutoPipelineRef.current) {
      suppressNextAutoPipelineRef.current = false;
      return;
    }
    const s = (startLocation || '').trim();
    const e = (endLocation || '').trim();
    if (!s || !e) return;
    const routeKey = `${s}|${e}`;
    if (lastRoutePipelineKeyRef.current === routeKey) return;
    const t = setTimeout(() => {
      runRoutePipeline(s, e);
    }, 400);
    return () => clearTimeout(t);
  }, [isChatOpen, isSelectingLocation, startLocation, endLocation, runRoutePipeline, chatEnabled]);

  // Handle map click for location selection (address already geocoded by GoogleMap)
  const handleMapClick = useCallback((location: Location, address?: string) => {
    // Prevent map clicks from updating addresses when chat is open
    if (isChatOpen) {
      return;
    }
    if (isSelectingLocation) {
      const finalAddress = address || `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`;
      handleLocationSelect(location, finalAddress, isSelectingLocation);
      return;
    }
    // Tapping on map area should hide route details panel.
    if (!isLocationPanelCollapsed) {
      setIsLocationPanelCollapsed(true);
    }
  }, [isSelectingLocation, handleLocationSelect, isChatOpen, isLocationPanelCollapsed]);

  // Parse route from text (e.g., "from X to Y")
  const parseRouteFromText = (text: string): { start: string; end: string } | null => {
    // Clean up common prefixes first
    let cleanText = text
      .replace(/^(?:plan\s+)?(?:a\s+)?(?:route|trip|journey|drive)\s+/i, '')
      .trim();

    // Pattern: "from X to Y"
    const fromToPattern = /^from\s+(.+?)\s+to\s+(.+?)(?:\s+with|\s+stopping|\s+via|\s*$)/i;
    let match = cleanText.match(fromToPattern);

    if (match && match[1] && match[2]) {
      return {
        start: match[1].trim(),
        end: match[2].trim()
      };
    }

    // Alternative pattern: "X to Y" (without "from")
    const simplePattern = /^(.+?)\s+to\s+(.+?)(?:\s+with|\s+stopping|\s+via|\s*$)/i;
    match = cleanText.match(simplePattern);

    if (match && match[1] && match[2]) {
      // Make sure start doesn't contain route-related words
      const start = match[1].trim();
      if (!start.match(/^(?:plan|route|trip|journey|drive)$/i)) {
        return {
          start: start,
          end: match[2].trim()
        };
      }
    }

    return null;
  };

  // Handle sending a message. When skipUserMessage is true, the user message is not shown in chat (e.g. filter clicks).
  const handleSendMessage = async (textOverride?: string, skipUserMessage?: boolean) => {
    const text = textOverride || inputText;
    if (!text.trim() || isLoading) return;

    // Initialize account if needed
    if (user?.uid) {
      await getOrInitializeAccount(user.uid);

      // Check if account can use the app
      if (!canUseApp(user.uid)) {
        setShowUpgradePrompt(true);
        setIsChatOpen(true);
        const upgradeMsg: Message = {
          id: Date.now().toString(),
          role: Role.ASSISTANT,
          text: "⚠️ You've used all your free responses. Upgrade to Standard ($14.99/month) or Premium ($19.99/month) to continue using RouteX.",
          timestamp: Date.now()
        };
        setMessages(prev => [...prev, upgradeMsg]);
        return;
      }
    }

    if (!skipUserMessage) {
      const userMsg: Message = {
        id: Date.now().toString(),
        role: Role.USER,
        text: text,
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, userMsg]);
    }
    // Clear input text and voice transcript immediately when sending
    if (!textOverride) {
      setInputText('');
      accumulatedTranscriptRef.current = ''; // Clear voice transcript to prevent it from reappearing
      isMessageSentRef.current = true; // Mark that a message was sent to prevent onresult from restoring text
    }
    setIsLoading(true);

    try {
      const isPreferredEnglish = (preferredLanguage || '').toLowerCase().startsWith('en');
      const userTextForDisplay = text;
      let userTextForAgent = text;

      // Translate user -> English before sending to LangGraph (agent stays English).
      if (!isPreferredEnglish) {
        try {
          const translated = await translateText(
            userTextForAgent,
            preferredLanguage || navigator.language || 'en-US',
            'en'
          );
          if (translated?.translated_text) {
            userTextForAgent = translated.translated_text;
          }
        } catch (e) {
          // If translation fails, fall back to original user text.
          console.warn('User->EN translation failed, sending original:', e);
        }
      }

      const response = await sendChatMessage({
        message: userTextForAgent,
        session_id: sessionId,
        start: startLocation,
        end: endLocation,
        current_preferences: selectedFilters.join(','),
        language: preferredLanguage || navigator.language || 'en-US',
        travel_mode: tripConfig?.travelMode,
        radius: tripConfig?.radius,
        max_time: tripConfig?.maxTime,
        number_of_people: tripConfig?.numberOfPeople
      });

      // Check if response contains route suggestions (from agent)
      if (response.suggestions && response.suggestions.length > 0) {
        if (response.ranked_places && response.ranked_places.length > 0) {
          setRankedPlaces(response.ranked_places);
        } else {
          setRankedPlaces([]);
        }

        // Find the suggestion with the maximum number of stops (start visualization from that route)
        const getStopCount = (s: typeof response.suggestions[0]) =>
          s.num_stops ?? (s.stops?.length ?? (s.suggested_stops?.length ?? 0));
        let maxStopsIndex = 0;
        let maxStops = getStopCount(response.suggestions[0]);
        for (let i = 1; i < response.suggestions.length; i++) {
          const count = getStopCount(response.suggestions[i]);
          if (count > maxStops) {
            maxStops = count;
            maxStopsIndex = i;
          }
        }

        const suggestionsSnapshot: RouteSuggestion[] =
          typeof structuredClone === 'function'
            ? structuredClone(response.suggestions)
            : (JSON.parse(JSON.stringify(response.suggestions)) as RouteSuggestion[]);

        setRouteSuggestions(suggestionsSnapshot);
        setActiveRouteSuggestion(maxStopsIndex);

        void getConfig()
          .then((c) =>
            warmChatStopPhotosForSuggestions(
              suggestionsSnapshot,
              c.google_maps_api_key ?? null,
            ),
          )
          .catch(() => warmChatStopPhotosForSuggestions(suggestionsSnapshot, null));

        // Fetch the fastest route (without stops) for comparison — must not throw or we skip
        // appending the route assistant message and ChatRouteSuggestions never render.
        if (startLocation && endLocation) {
          setIsRouteLoading(true);
          try {
            try {
              const fastest = await getFastestRoute(startLocation, endLocation);
              setFastestRoute(fastest);
            } catch (e) {
              console.warn('[RouteX] Fastest route fetch failed; still showing route options in chat', e);
            }
          } finally {
            setIsRouteLoading(false);
          }
        }

        // Set polyline and places from the route with max stops
        const selectedSuggestion = suggestionsSnapshot[maxStopsIndex];
        setRoutePolyline(selectedSuggestion.polyline);

        // Set start/end coords from route legs so "Go!" and "Start Journey" work when routes are suggested
        if (selectedSuggestion.legs?.length) {
          const firstLeg = selectedSuggestion.legs[0];
          const lastLeg = selectedSuggestion.legs[selectedSuggestion.legs.length - 1];
          if (firstLeg?.start_location) setStartLocationCoords(firstLeg.start_location);
          if (lastLeg?.end_location) setEndLocationCoords(lastLeg.end_location);
        }

        const stops = selectedSuggestion.stops || selectedSuggestion.suggested_stops;
        const numStops = selectedSuggestion.num_stops || stops?.length || 0;

        if (stops && stops.length > 0) {
          const placesFromStops: Place[] = stops.map((stop, index) => {
            if ('place_id' in stop && 'name' in stop && 'location' in stop) {
              return stop as Place;
            }
            const latLngStop = stop as {
              lat: number;
              lng: number;
              name?: string;
              place_id?: string;
              type?: string;
              types?: string[];
              found_for_preference?: string;
              rating?: number;
              formatted_address?: string;
            };
            return {
              place_id: latLngStop.place_id || `stop-${index}`,
              name: latLngStop.name || `Stop ${index + 1}`,
              location: { lat: latLngStop.lat, lng: latLngStop.lng },
              types: latLngStop.types || (latLngStop.type ? [latLngStop.type] : undefined),
              found_for_preference: latLngStop.found_for_preference,
              rating: latLngStop.rating,
              formatted_address: latLngStop.formatted_address,
            };
          });
          setSuggestedPlaces(placesFromStops);
        }

        // Add assistant message with route info (refer to selected route with max stops)
        let routeMsgText = response.reasoning
          ? `🗺️ I found ${suggestionsSnapshot.length} route option(s) for you!\n\n${response.reasoning}\n\nThe route with most stops has ${numStops} stops and takes about ${Math.round(selectedSuggestion.total_time)} minutes.`
          : `🗺️ I found ${suggestionsSnapshot.length} route option(s) for you! The route with most stops has ${numStops} stops and takes about ${Math.round(selectedSuggestion.total_time)} minutes.`;

        // Translate EN -> preferred language for UI + TTS (agent response is English)
        if (!isPreferredEnglish && routeMsgText.trim()) {
          try {
            const translated = await translateText(routeMsgText, 'en', preferredLanguage);
            if (translated?.translated_text) routeMsgText = translated.translated_text;
          } catch (e) {
            console.warn('EN->preferred translation failed for route msg:', e);
          }
        }

        const routeMsg: Message = {
          id: (Date.now() + 1).toString(),
          role: Role.ASSISTANT,
          text: routeMsgText,
          timestamp: Date.now(),
          routeSuggestions: suggestionsSnapshot,
        };

        if (user?.uid && routeMsgText.trim()) {
          void decrementResponseCount(user.uid);
        }

        setMessages(prev => [...prev, routeMsg]);
        // TTS will be handled by the centralized useEffect watching messages
        return; // Exit early since we handled route suggestions
      }

      // Update filters if preferences were extracted
      let allPreferences = [...selectedFilters];
      if (response.preferences && response.preferences.length > 0) {
        allPreferences = [...new Set([...selectedFilters, ...response.preferences])];
        setSelectedFilters(allPreferences);
      }

      if (response.ranked_places && response.ranked_places.length > 0) {
        setRankedPlaces(response.ranked_places);
      } else {
        setRankedPlaces([]);
      }

      let botMsgText = response.response || "I'm here to help you plan your journey!";

      // Translate EN -> preferred language for UI + TTS
      if (!isPreferredEnglish && botMsgText.trim()) {
        try {
          const translated = await translateText(botMsgText, 'en', preferredLanguage);
          if (translated?.translated_text) botMsgText = translated.translated_text;
        } catch (e) {
          console.warn('EN->preferred translation failed for bot msg:', e);
        }
      }
      const botMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: Role.ASSISTANT,
        text: botMsgText,
        timestamp: Date.now()
      };

      // Decrement response count when assistant message is received
      if (user?.uid && botMsgText.trim()) {
        void decrementResponseCount(user.uid);
      }

      setMessages(prev => [...prev, botMsg]);
      // TTS will be handled by the centralized useEffect watching messages

      // Check if this is a route request and update locations if needed
      // Parse route from the English text we sent to the agent (more reliable for patterns).
      const routeInfo = parseRouteFromText(userTextForAgent);
      if (routeInfo && !isChatOpen) {
        // Remove welcome message if it exists when route is detected
        setMessages(prev => prev.filter(msg => msg.id !== 'welcome'));
        // Update start/end locations
        setStartLocation(routeInfo.start);
        setEndLocation(routeInfo.end);
        // Agent will handle route planning through the chat response
      }

      // If new preferences were extracted and we already have start/end, ask agent to plan route
      // But only if we don't already have route suggestions from this response
      if (response.preferences && response.preferences.length > 0 && startLocation && endLocation && !response.suggestions) {
        // Send a follow-up message to agent to plan route with preferences
        const preferenceText = allPreferences.join(', ');
        // Use setTimeout to avoid calling handleSendMessage recursively
        setTimeout(() => {
          handleSendMessage(`Plan route from ${startLocation} to ${endLocation} with ${preferenceText}`);
        }, 100);
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMsgText = "Sorry, I encountered an error. Please try again.";
      const errorMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: Role.ASSISTANT,
        text: errorMsgText,
        timestamp: Date.now()
      };

      // Decrement response count when assistant error message is received
      if (user?.uid && errorMsgText.trim()) {
        void decrementResponseCount(user.uid);
      }

      setMessages(prev => [...prev, errorMsg]);
      // TTS will be handled by the centralized useEffect watching messages
    } finally {
      setIsLoading(false);
      // Clear input text and voice transcript after message is sent (ready for next conversation)
      if (!textOverride) {
        setInputText('');
        accumulatedTranscriptRef.current = ''; // Clear voice transcript to prevent it from reappearing
      }
      // Reset the flag after a delay to allow onresult events to finish processing
      setTimeout(() => {
        isMessageSentRef.current = false;
      }, 500);
      // Ensure focus stays on chat input after message is sent
      setTimeout(() => {
        if (inputRef.current && isChatOpen) {
          inputRef.current.focus();
        }
      }, 100);
    }
  };

  // Handle form submission
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event from bubbling up
    handleSendMessage();
    // Keep focus on chat input after sending
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  // Handle place selection
  const handlePlaceSelect = (placeId: string) => {
    const place = suggestedPlaces.find(p => p.place_id === placeId);
    if (place) {
      handleSendMessage(`Add ${place.name} as a stop on my route`);
    }
  };

  // Handle place click on map - show details modal
  const handlePlaceClick = (place: Place) => {
    setSelectedPlace(place);
    setIsPlaceDetailsOpen(true);
  };

  // Recalculate route with updated waypoints
  const recalculateRouteWithWaypoints = useCallback(async (places: Place[], currentRouteSuggestions: RouteSuggestion[], currentActiveIndex: number) => {
    if (!startLocation || !endLocation || !startLocationCoords || !endLocationCoords) {
      return;
    }

    // Only recalculate if we have route suggestions (not for simple routes)
    if (currentRouteSuggestions.length === 0 || currentActiveIndex < 0) {
      return;
    }



    try {
      const travelMode = tripConfig?.travelMode || 'DRIVING';
      const waypoints = places.map((place) => ({
        lat: place.location.lat,
        lng: place.location.lng,
      }));
      const recalculatedRoute = await recalculateRoute({
        start: startLocation,
        end: endLocation,
        mode: travelMode,
        waypoints,
      });

      if (!recalculatedRoute.polyline || !recalculatedRoute.legs) {
        console.error('Route recalculation returned incomplete data');
        return;
      }

      const polyline = recalculatedRoute.polyline;
      const formattedLegs = recalculatedRoute.legs;
      const totalTimeMinutes = formattedLegs.reduce(
        (sum, leg) => sum + (leg.duration?.value || 0),
        0
      ) / 60;

      // Update the active route suggestion if we have one
      if (currentRouteSuggestions.length > 0 && currentActiveIndex >= 0) {
        // First clear the polyline to force map update
        setRoutePolyline(undefined);

        // Then update route suggestions
        setRouteSuggestions(prev => {
          const updated = [...prev];
          if (updated[currentActiveIndex]) {
            updated[currentActiveIndex] = {
              ...updated[currentActiveIndex],
              polyline: polyline,
              legs: formattedLegs,
              total_time: totalTimeMinutes,
              num_stops: places.length,
              stops: places,
              suggested_stops: places.map(p => ({
                lat: p.location.lat,
                lng: p.location.lng,
                name: p.name,
                place_id: p.place_id,
                type: p.types?.[0] || p.found_for_preference || 'place'
              }))
            };
          }
          return updated;
        });

        // Update the route polyline after a brief delay to ensure state updates
        setTimeout(() => {
          setRoutePolyline(polyline);
        }, 50);
      } else {
        // If no route suggestions, just update the polyline directly
        setRoutePolyline(undefined);
        setTimeout(() => {
          setRoutePolyline(polyline);
        }, 50);
      }
    } catch (error) {
      console.error('Error recalculating route:', error);
    }
  }, [startLocation, endLocation, startLocationCoords, endLocationCoords, tripConfig]);

  const handleAddPlaceToRoute = useCallback((place: Place) => {
    setSuggestedPlaces((prev) => {
      if (prev.some((p) => p.place_id === place.place_id)) {
        return prev;
      }
      const updated = [...prev, place];
      setRouteSuggestions((currentSuggestions) => {
        setTimeout(() => {
          recalculateRouteWithWaypoints(
            updated,
            currentSuggestions,
            activeRouteSuggestionRef.current
          );
        }, 100);
        return currentSuggestions;
      });
      return updated;
    });
  }, [recalculateRouteWithWaypoints]);

  // Remove place from route
  const handleRemovePlace = useCallback((placeId: string) => {
    setSuggestedPlaces((prev) => {
      const updated = prev.filter((p) => p.place_id !== placeId);
      // Recalculate route with updated places
      if (updated.length !== prev.length) {
        // Get current state values to pass to recalculation
        setRouteSuggestions(currentSuggestions => {
          setTimeout(() => {
            recalculateRouteWithWaypoints(updated, currentSuggestions, activeRouteSuggestionRef.current);
          }, 100);
          return currentSuggestions; // Return unchanged for now, will be updated by recalculateRouteWithWaypoints
        });
      }
      return updated;
    });
  }, [recalculateRouteWithWaypoints]);

  // Remove place from a specific suggested route (for chat route cards).
  const handleRemovePlaceFromRoute = useCallback(
    (routeIndex: number, placeId: string) => {
      setRouteSuggestions((currentSuggestions) => {
        const suggestion = currentSuggestions[routeIndex];
        if (!suggestion) return currentSuggestions;
        const rawStops = suggestion.stops || suggestion.suggested_stops || [];
        const routeStops: Place[] = rawStops.map((stop, idx) => {
          if ('place_id' in stop && 'name' in stop && 'location' in stop) {
            return stop as Place;
          }
          const s = stop as { lat: number; lng: number; name?: string; place_id?: string; type?: string; types?: string[] };
          return {
            place_id: s.place_id || `stop-${idx}`,
            name: s.name || `Stop ${idx + 1}`,
            location: { lat: s.lat, lng: s.lng },
            types: s.types || (s.type ? [s.type] : undefined),
          };
        });
        const updatedRouteStops = routeStops.filter((p) => p.place_id !== placeId);
        if (updatedRouteStops.length === routeStops.length) return currentSuggestions;

        const updatedSuggestions = [...currentSuggestions];
        updatedSuggestions[routeIndex] = {
          ...suggestion,
          stops: updatedRouteStops,
          suggested_stops: updatedRouteStops.map((p) => ({
            lat: p.location.lat,
            lng: p.location.lng,
            name: p.name,
            place_id: p.place_id,
            type: p.types?.[0] || p.found_for_preference || 'place',
          })),
          num_stops: updatedRouteStops.length,
        };

        // If this route is currently active on the map, update visible stop list immediately.
        if (activeRouteSuggestionRef.current === routeIndex) {
          setSuggestedPlaces(updatedRouteStops);
        }

        setTimeout(() => {
          recalculateRouteWithWaypoints(
            updatedRouteStops,
            updatedSuggestions,
            routeIndex
          );
        }, 100);

        return updatedSuggestions;
      });
    },
    [recalculateRouteWithWaypoints]
  );

  // Keep chat route cards in sync with latest route edits/recalculations.
  useEffect(() => {
    if (!routeSuggestions.length) return;
    setMessages((prev) => {
      let targetIndex = -1;
      for (let i = prev.length - 1; i >= 0; i--) {
        const msg = prev[i];
        if (msg && msg.routeSuggestions && msg.routeSuggestions.length > 0) {
          targetIndex = i;
          break;
        }
      }
      if (targetIndex === -1) return prev;
      const target = prev[targetIndex];
      if (!target) return prev;
      if (target.routeSuggestions === routeSuggestions) return prev;
      const next = [...prev];
      next[targetIndex] = {
        ...target,
        routeSuggestions,
      };
      return next;
    });
  }, [routeSuggestions]);

  // Reorder stops in Directions tab (called by @dnd-kit onDragEnd)
  const handleDirectionsDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const ids = suggestedPlaces.map((p, i) => p.place_id || `stop-${i}`);
      const oldIndex = ids.indexOf(active.id as string);
      const newIndex = ids.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(suggestedPlaces, oldIndex, newIndex);
      reorderedStopsRef.current = reordered;
      setSuggestedPlaces(reordered);
      setRouteSuggestions((current) => {
        if (reorderedStopsRef.current) {
          setTimeout(() => {
            recalculateRouteWithWaypoints(
              reorderedStopsRef.current!,
              current,
              activeRouteSuggestionRef.current
            );
            reorderedStopsRef.current = null;
          }, 100);
        }
        return current;
      });
    },
    [suggestedPlaces, recalculateRouteWithWaypoints]
  );

  // Build Google Maps navigation URL for the selected route (shared for Directions tab and map Start button)
  const buildGoogleMapsUrl = useCallback((): string => {
    if (!startLocationCoords || !endLocationCoords) return '';
    const origin = `${startLocationCoords.lat},${startLocationCoords.lng}`;
    const destination = `${endLocationCoords.lat},${endLocationCoords.lng}`;
    let url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}`;
    if (suggestedPlaces.length > 0) {
      const waypoints = suggestedPlaces.map(p => `${p.location.lat},${p.location.lng}`).join('|');
      url += `&waypoints=${encodeURIComponent(waypoints)}`;
    }
    return url;
  }, [startLocationCoords, endLocationCoords, suggestedPlaces]);

  const buildAppleMapsUrl = useCallback((): string => {
    const fallback = 'https://maps.apple.com/';
    // Apple parses human-readable addresses for multi-stop chains more reliably than raw lat/lng.
    const s =
      startLocation ||
      (startLocationCoords
        ? `${startLocationCoords.lat},${startLocationCoords.lng}`
        : '');
    const finalDestination =
      endLocation ||
      (endLocationCoords
        ? `${endLocationCoords.lat},${endLocationCoords.lng}`
        : '');
    if (!s && !finalDestination) return fallback;

    const stopSegments = suggestedPlaces.map((p) => {
      return (
        p.formatted_address ||
        p.name ||
        `${p.location.lat},${p.location.lng}`
      );
    });
    // Apple Maps supports daddr chains with "to:" separators.
    const routeSegments = [...stopSegments, finalDestination].filter(Boolean);
    const routeChainEncoded = routeSegments
      .map((segment) => encodeURIComponent(segment))
      .join('%20to:%20');

    const query: string[] = [];
    if (s) query.push(`saddr=${encodeURIComponent(s)}`);
    if (routeChainEncoded) query.push(`daddr=${routeChainEncoded}`);
    const mode = tripConfig?.travelMode || 'DRIVING';
    query.push(
      `dirflg=${mode === 'WALKING' ? 'w' : mode === 'TRANSIT' ? 'r' : 'd'}`
    );
    return `${fallback}?${query.join('&')}`;
  }, [
    startLocationCoords,
    endLocationCoords,
    startLocation,
    endLocation,
    tripConfig,
    suggestedPlaces,
  ]);

  const getRoutePointsForApple = useCallback((): string[] => {
    const start =
      startLocation ||
      (startLocationCoords
        ? `${startLocationCoords.lat},${startLocationCoords.lng}`
        : '');
    const stops = suggestedPlaces.map((p) => {
      return (
        p.formatted_address ||
        p.name ||
        `${p.location.lat},${p.location.lng}`
      );
    });
    const destination =
      endLocation ||
      (endLocationCoords
        ? `${endLocationCoords.lat},${endLocationCoords.lng}`
        : '');
    return [start, ...stops, destination].filter(Boolean);
  }, [
    startLocation,
    startLocationCoords,
    suggestedPlaces,
    endLocation,
    endLocationCoords,
  ]);

  const buildAppleLegUrl = useCallback(
    (fromPoint: string, toPoint: string): string => {
      const mode = tripConfig?.travelMode || 'DRIVING';
      const params = new URLSearchParams();
      params.set('saddr', fromPoint);
      params.set('daddr', toPoint);
      params.set('dirflg', mode === 'WALKING' ? 'w' : mode === 'TRANSIT' ? 'r' : 'd');
      return `https://maps.apple.com/?${params.toString()}`;
    },
    [tripConfig]
  );

  const handleStartJourney = useCallback((app: 'google' | 'apple' = 'google') => {
    if (app === 'apple') {
      const points = getRoutePointsForApple();
      if (points.length >= 2) {
        // Start leg-by-leg Apple navigation to ensure all stops are covered.
        setAppleLegPoints(points);
        setCurrentAppleLegIndex(0);
        const firstLegUrl = buildAppleLegUrl(points[0], points[1]);
        window.open(firstLegUrl, '_blank');
        return;
      }
      // Fallback for edge cases
      const fallback = buildAppleMapsUrl();
      if (fallback) window.open(fallback, '_blank');
      return;
    }
    const mapsUrl = buildGoogleMapsUrl();
    if (mapsUrl) window.open(mapsUrl, '_blank');
  }, [
    buildGoogleMapsUrl,
    buildAppleMapsUrl,
    getRoutePointsForApple,
    buildAppleLegUrl,
  ]);

  const handleGoClick = useCallback(() => {
    setShowGoConfirmDialog(true);
  }, []);

  const bottomTabs = useMemo(() => {
    if (!chatEnabled) {
      return DEFAULT_TABS.filter((tab) => tab.id === 'map');
    }
    return DEFAULT_TABS;
  }, [chatEnabled]);

  const handleGoConfirmYes = useCallback(() => {
    setShowGoConfirmDialog(false);
    setShowBookingPlaceholder(true);
  }, []);

  const handleGoConfirmNo = useCallback(() => {
    setShowGoConfirmDialog(false);
    setShowNavigationAppDialog(true);
  }, []);

  const handleResetSessionConfirmYes = useCallback(() => {
    // Hard-stop any ongoing voice activity before resetting the session.
    isButtonHeldRef.current = false;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort();
      } catch {
        try {
          recognitionRef.current.stop();
        } catch {
          // ignore
        }
      }
    }
    setIsListening(false);
    accumulatedTranscriptRef.current = '';

    if (currentAudioRef.current) {
      try {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
      } catch {
        // ignore
      }
      currentAudioRef.current = null;
    }
    if (ttsGenerationAbortRef.current) {
      ttsGenerationAbortRef.current.abort();
      ttsGenerationAbortRef.current = null;
    }
    isGeneratingTTSRef.current = false;
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }

    // Rotate session id so the backend checkpointer starts a fresh thread.
    resetSessionId();

    setShowResetSessionConfirmDialog(false);
    onBackToWelcome();
  }, [onBackToWelcome]);

  const handleResetSessionConfirmNo = useCallback(() => {
    setShowResetSessionConfirmDialog(false);
  }, []);

  const currentJourneySignature = useMemo(() => {
    if (!startLocationCoords || !endLocationCoords) return '';
    return [
      startLocationCoords.lat.toFixed(6),
      startLocationCoords.lng.toFixed(6),
      endLocationCoords.lat.toFixed(6),
      endLocationCoords.lng.toFixed(6),
      routePolyline || '',
      suggestedPlaces
        .map((p) => `${p.place_id}:${p.location.lat.toFixed(6)},${p.location.lng.toFixed(6)}`)
        .join('|'),
    ].join('::');
  }, [startLocationCoords, endLocationCoords, routePolyline, suggestedPlaces]);
  const isCurrentJourneySaved =
    !!savedJourneyIdForCurrentRoute &&
    !!currentJourneySignature &&
    savedJourneySignature === currentJourneySignature;

  // If the route changes after being saved, treat it as a new unsaved draft immediately.
  useEffect(() => {
    if (!savedJourneyIdForCurrentRoute) return;
    if (!currentJourneySignature) return;
    if (savedJourneySignature === currentJourneySignature) return;
    setSavedJourneyIdForCurrentRoute(null);
    setSavedJourneySignature('');
  }, [savedJourneyIdForCurrentRoute, savedJourneySignature, currentJourneySignature]);

  const handleSaveJourney = useCallback(async () => {
    if (!user?.uid || !startLocationCoords || !endLocationCoords) return;
    const currentSignature = currentJourneySignature;
    const isCurrentlySaved = isCurrentJourneySaved;

    const baseName =
      (startLocation || '') && (endLocation || '')
        ? `${(startLocation || '').slice(0, 25)}${(startLocation || '').length > 25 ? '…' : ''} → ${(endLocation || '').slice(0, 25)}${(endLocation || '').length > 25 ? '…' : ''}`
        : 'Saved journey';
    try {
      if (isCurrentlySaved && savedJourneyIdForCurrentRoute) {
        await deleteSavedJourney(user.uid, savedJourneyIdForCurrentRoute);
        setSavedJourneyIdForCurrentRoute(null);
        setSavedJourneySignature('');
        setSavedJourneyMessageTone('success');
        setSavedJourneyMessage('Journey unsaved!');
      } else {
        const saved = await createSavedJourney(user.uid, {
          name: baseName,
          startAddress: startLocation || '',
          endAddress: endLocation || '',
          startCoords: startLocationCoords,
          endCoords: endLocationCoords,
          places: suggestedPlaces,
          savedAt: Date.now(),
          routePolyline: routePolyline,
          routeSuggestions: routeSuggestions?.length ? routeSuggestions : undefined,
        });
        setSavedJourneyIdForCurrentRoute(saved.id);
        setSavedJourneySignature(currentSignature);
        setSavedJourneyMessageTone('success');
        setSavedJourneyMessage('Journey saved!');
      }
      setTimeout(() => setSavedJourneyMessage(null), 3000);
    } catch (err) {
      console.error('[RouteX] Failed to save journey:', err);
      setSavedJourneyMessageTone('error');
      setSavedJourneyMessage('Failed to save journey');
      setTimeout(() => setSavedJourneyMessage(null), 3000);
    }
  }, [user?.uid, startLocationCoords, endLocationCoords, startLocation, endLocation, suggestedPlaces, routePolyline, routeSuggestions, savedJourneyIdForCurrentRoute, currentJourneySignature, isCurrentJourneySaved]);

  const selectFastestRouteOption = useCallback(() => {
    if (!fastestRoute) return;
    setActiveRouteSuggestion(-1);
    setRoutePolyline(fastestRoute.polyline);
    setSuggestedPlaces([]); // No stops for fastest route
  }, [fastestRoute]);

  const mapPlaces = useMemo(() => {
    const seen = new Set<string>();
    const merged: Place[] = [];
    for (const place of suggestedPlaces) {
      if (!place?.place_id || seen.has(place.place_id)) continue;
      seen.add(place.place_id);
      merged.push({ ...place, marker_kind: 'stop' });
    }
    for (const place of rankedPlaces) {
      if (!place?.place_id || seen.has(place.place_id)) continue;
      seen.add(place.place_id);
      merged.push({ ...place, marker_kind: 'ranked' });
    }
    return merged;
  }, [suggestedPlaces, rankedPlaces]);

  const directionsActiveLegs = useMemo(() => {
    return activeRouteSuggestion === -1
      ? fastestRoute?.legs
      : routeSuggestions[activeRouteSuggestion]?.legs;
  }, [activeRouteSuggestion, fastestRoute, routeSuggestions]);

  const directionsEtaByStopId = useMemo(
    () =>
      buildEtaByStopId(
        suggestedPlaces,
        directionsActiveLegs,
        stayOverridesByStopId,
        directionsStartTimeMode,
        directionsStartDateTime
      ),
    [
      suggestedPlaces,
      directionsActiveLegs,
      stayOverridesByStopId,
      directionsStartTimeMode,
      directionsStartDateTime,
    ]
  );

  const selectSuggestedRouteOption = useCallback((index: number) => {
    const suggestion = routeSuggestions[index];
    if (!suggestion) return;
    setActiveRouteSuggestion(index);
    setRoutePolyline(suggestion.polyline);

    const stops = suggestion.stops || suggestion.suggested_stops;
    if (stops && stops.length > 0) {
      const places: Place[] = stops.map((stop, idx) => {
        if ('place_id' in stop && 'name' in stop && 'location' in stop) {
          return stop as Place;
        }
        const latLngStop = stop as {
          lat: number;
          lng: number;
          name?: string;
          place_id?: string;
          type?: string;
          types?: string[];
          found_for_preference?: string;
          rating?: number;
          formatted_address?: string;
        };
        return {
          place_id: latLngStop.place_id || `stop-${idx}`,
          name: latLngStop.name || `Stop ${idx + 1}`,
          location: { lat: latLngStop.lat, lng: latLngStop.lng },
          types: latLngStop.types || (latLngStop.type ? [latLngStop.type] : undefined),
          found_for_preference: latLngStop.found_for_preference,
          rating: latLngStop.rating,
          formatted_address: latLngStop.formatted_address,
        };
      });
      setSuggestedPlaces(places);
    } else {
      setSuggestedPlaces([]);
    }
  }, [routeSuggestions]);

  const handleLoadSavedJourney = useCallback((journey: SavedJourney) => {
    // Prevent the "fresh session" auto-pipeline/greeting from firing for saved routes.
    suppressNextAutoPipelineRef.current = true;
    lastRoutePipelineKeyRef.current = `${(journey.startAddress || '').trim()}|${(journey.endAddress || '').trim()}`;

    setStartLocation(journey.startAddress);
    setEndLocation(journey.endAddress);
    setStartLocationCoords(journey.startCoords);
    setEndLocationCoords(journey.endCoords);
    setSuggestedPlaces(journey.places);
    setRoutePolyline(journey.routePolyline ?? undefined);
    setRouteSuggestions(journey.routeSuggestions ?? []);
    setSavedJourneyIdForCurrentRoute(journey.id);
    const loadedSignature = [
      journey.startCoords.lat.toFixed(6),
      journey.startCoords.lng.toFixed(6),
      journey.endCoords.lat.toFixed(6),
      journey.endCoords.lng.toFixed(6),
      journey.routePolyline || '',
      (journey.places || [])
        .map((p) => `${p.place_id}:${p.location.lat.toFixed(6)},${p.location.lng.toFixed(6)}`)
        .join('|'),
    ].join('::');
    setSavedJourneySignature(loadedSignature);
    setActiveTab('map');
  }, []);

  useEffect(() => {
    if (!preloadedJourney) return;
    if (hasAppliedPreloadedJourneyRef.current) return;
    hasAppliedPreloadedJourneyRef.current = true;
    handleLoadSavedJourney(preloadedJourney);
  }, [preloadedJourney, handleLoadSavedJourney]);

  // Render content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'contribute':
        return (
          <div className="p-4 pb-24">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Contribute</h2>
            <p className="text-sm text-gray-500 mb-6">Help improve places on Google Maps.</p>
            <div className="space-y-3">
              <a
                href="https://www.google.com/maps/contrib"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 w-full p-4 rounded-xl border-2 border-gray-200 text-gray-800 font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors text-left"
              >
                <span className="material-symbols-outlined text-[28px] text-green-600">add_location</span>
                <div>
                  <p className="font-semibold text-gray-900">Add place</p>
                  <p className="text-sm text-gray-500">Add a missing place to the map</p>
                </div>
                <span className="material-symbols-outlined text-gray-400 ml-auto">open_in_new</span>
              </a>
              <a
                href="https://www.google.com/maps"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 w-full p-4 rounded-xl border-2 border-gray-200 text-gray-800 font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors text-left"
              >
                <span className="material-symbols-outlined text-[28px] text-amber-600">rate_review</span>
                <div>
                  <p className="font-semibold text-gray-900">Add review</p>
                  <p className="text-sm text-gray-500">Share your experience at a place</p>
                </div>
                <span className="material-symbols-outlined text-gray-400 ml-auto">open_in_new</span>
              </a>
              <a
                href="https://www.google.com/maps"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 w-full p-4 rounded-xl border-2 border-gray-200 text-gray-800 font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors text-left"
              >
                <span className="material-symbols-outlined text-[28px] text-blue-600">add_a_photo</span>
                <div>
                  <p className="font-semibold text-gray-900">Add photo with score</p>
                  <p className="text-sm text-gray-500">Upload a photo and rate a place</p>
                </div>
                <span className="material-symbols-outlined text-gray-400 ml-auto">open_in_new</span>
              </a>
            </div>
          </div>
        );

      case 'settings':
        return (
          <div className="p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Settings</h2>

            {/* Profile Section */}
            <ProfileSection
              onLoadSavedJourney={handleLoadSavedJourney}
              preferredLanguage={preferredLanguage}
              languageOptions={LANGUAGE_OPTIONS}
              onPreferredLanguageChange={onPreferredLanguageChange}
            />

            {/* Trip Configuration */}
            {tripConfig && (
              <div className="mt-6 space-y-2">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Trip Configuration</h3>
                <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-4">
                  {/* Trip Type */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-blue-600">today</span>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">Trip Type</p>
                        <p className="text-sm text-gray-500">
                          {tripType === 'single-day' ? 'Single Day Trip' :
                           tripType === 'multiple-days' ? 'Multiple Days Trip' :
                           tripType === 'deep-planning' ? 'Deep Planning' : 'Not selected'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Travel Mode */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-blue-600">
                        {tripConfig.travelMode === 'DRIVING' ? 'directions_car' :
                         tripConfig.travelMode === 'BICYCLING' ? 'directions_bike' :
                         tripConfig.travelMode === 'WALKING' ? 'directions_walk' :
                         'directions_transit'}
                      </span>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">Travel Mode</p>
                        <p className="text-sm text-gray-500">
                          {tripConfig.travelMode === 'DRIVING' ? 'Car' :
                           tripConfig.travelMode === 'BICYCLING' ? 'Bike' :
                           tripConfig.travelMode === 'WALKING' ? 'Walk' :
                           'Public Transport'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Search Radius */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-blue-600">location_searching</span>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">Search Radius</p>
                        <p className="text-sm text-gray-500">{tripConfig.radius} km</p>
                      </div>
                    </div>
                  </div>

                  {/* Maximum Travel Time */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-blue-600">schedule</span>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">Maximum Travel Time</p>
                        <p className="text-sm text-gray-500">
                          {tripConfig.maxTime < 60
                            ? `${tripConfig.maxTime} min`
                            : tripConfig.maxTime === 60
                            ? '1 hour'
                            : tripConfig.maxTime % 60 === 0
                            ? `${tripConfig.maxTime / 60} hours`
                            : `${Math.floor(tripConfig.maxTime / 60)}h ${tripConfig.maxTime % 60}m`}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* App Settings */}
            <div className="mt-6 space-y-2">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">App Settings</h3>
              <div className="flex items-center justify-between py-3 px-4 bg-white rounded-xl border border-gray-100">
                <div>
                  <p className="font-medium text-gray-900">Location Services</p>
                  <p className="text-sm text-gray-500">
                    {location ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
                <span className={`material-symbols-outlined ${location ? 'text-green-600' : 'text-gray-400'}`}>
                  {location ? 'check_circle' : 'cancel'}
                </span>
              </div>
              <p className="text-xs text-gray-400 pt-4">RouteX AI version 1.0.0</p>
            </div>
          </div>
        );

      default: // map
        return null;
    }
  };

  return (
    <div className="flex flex-col h-screen bg-white overflow-hidden relative font-sans">

      {/* Map layer - always mounted but hidden when not active to preserve state */}
      <div className={`absolute inset-0 z-10 ${activeTab === 'map' ? '' : 'invisible'}`}>
        <GoogleMap
          center={mapCenter}
          zoom={14}
          places={mapPlaces}
           polyline={
             startLocationCoords && endLocationCoords
               ? correctedRoutePolyline ?? routePolyline
               : routePolyline
           }
          fastestRoutePolyline={
            fastestRoute && routeSuggestions.length > 0 && activeRouteSuggestion !== -1
              ? fastestRoute.polyline
              : undefined
          }
          currentLocation={location ? { lat: location.latitude, lng: location.longitude } : undefined}
          startLocation={startLocationCoords}
          endLocation={endLocationCoords}
          isSelectingLocation={isSelectingLocation}
          onMapClick={handleMapClick}
          hasPreferences={routeSuggestions.length > 0 && activeRouteSuggestion >= 0}
          travelMode={tripConfig?.travelMode ?? 'DRIVING'}
          className="w-full h-full"
          onPlaceClick={handlePlaceClick}
        />

        {/* Only show map overlays when map tab is active */}
        {activeTab === 'map' && (
          <>
          {/* Block map interaction and show Planning... when chat closed and agent working */}
          {!isChatOpen && isLoading && (
            <>
              <div
                className="absolute inset-0 z-20 bg-black/10 pointer-events-auto cursor-wait flex items-center justify-center"
                aria-hidden
              >
                <div className="bg-white/95 backdrop-blur rounded-xl shadow-lg border border-gray-200 px-5 py-3 flex items-center gap-2">
                  <span
                    className="rounded-full border-2 border-blue-600 border-t-transparent animate-spin flex-shrink-0"
                    style={{ width: 22, height: 22 }}
                  />
                  <span className="text-gray-800 font-medium text-base">Planning...</span>
                </div>
              </div>
            </>
          )}
          {/* Route options ribbon + route details (stacked); under status bar only (preferences bar removed) */}
          <div className="absolute top-3 left-4 right-4 z-[30] pt-safe-top flex flex-col gap-2">
            {routeSuggestions.length >= 1 && (
              <div className="bg-white rounded-xl shadow-lg p-3 shrink-0">
                <MapRouteOptionsChips
                  routeSuggestions={routeSuggestions}
                  fastestRoute={fastestRoute}
                  activeRouteSuggestion={activeRouteSuggestion}
                  onSelectFastest={selectFastestRouteOption}
                  onSelectSuggested={selectSuggestedRouteOption}
                />
              </div>
            )}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300">
              {/* Collapse/Expand button */}
              <button
                onClick={() => setIsLocationPanelCollapsed(!isLocationPanelCollapsed)}
                className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 transition-colors border-b border-gray-100"
              >
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-gray-600 text-[20px]">
                    {isLocationPanelCollapsed ? 'expand_more' : 'expand_less'}
                  </span>
                  <span className="text-sm font-medium text-gray-700">
                    {isLocationPanelCollapsed ? 'Show Route Details' : 'Hide Route Details'}
                  </span>
                </div>
              </button>

              {/* Collapsible content */}
              <div className={`transition-all duration-300 ${
                isLocationPanelCollapsed ? 'max-h-0 opacity-0 overflow-hidden' : 'max-h-[min(70vh,36rem)] opacity-100 overflow-hidden'
              }`}>
                <div className="p-3 space-y-2 max-h-[min(70vh,36rem)] overflow-y-auto">
                  {suggestedPlaces.length > 0 && activeRouteSuggestion !== -1 && (
                    <div className="flex justify-start">
                      <DirectionsStartTimeRibbon
                        activeRouteSuggestion={activeRouteSuggestion}
                        directionsStartTimeMode={directionsStartTimeMode}
                        directionsStartDateTime={directionsStartDateTime}
                        onDirectionsStartTimeModeChange={setDirectionsStartTimeMode}
                        onDirectionsStartDateTimeChange={setDirectionsStartDateTime}
                        onCalendarOpenChange={setIsDirectionsCalendarOpen}
                      />
                    </div>
                  )}
                  {!isDirectionsCalendarOpen && (
                    <>
                      {/* Start location input */}
                      <LocationInput
                        value={startLocation}
                        onChange={(value) => handleLocationInputChange(value, 'start')}
                        onLocationSelect={(location, address) => handleLocationSelect(location, address, 'start')}
                        placeholder="Choose starting point"
                        icon="place"
                        iconColor="#34C759"
                        isStart={true}
                        disabled={isChatOpen}
                        onFocus={() => {
                          if (!isChatOpen) {
                            setIsSelectingLocation('start');
                          }
                        }}
                        onBlur={() => {
                          // Delay to allow click events to fire
                          setTimeout(() => setIsSelectingLocation(null), 200);
                        }}
                      />

                      {suggestedPlaces.length > 0 && (
                        <DirectionsRouteStopsBlock
                          variant="embedded"
                          startLocation={startLocation}
                          endLocation={endLocation}
                          suggestedPlaces={suggestedPlaces}
                          activeRouteSuggestion={activeRouteSuggestion}
                          directionsStartTimeMode={directionsStartTimeMode}
                          directionsStartDateTime={directionsStartDateTime}
                          onDirectionsStartTimeModeChange={setDirectionsStartTimeMode}
                          onDirectionsStartDateTimeChange={setDirectionsStartDateTime}
                          etaByStopId={directionsEtaByStopId}
                          setStayOverridesByStopId={setStayOverridesByStopId}
                          directionsSensors={directionsSensors}
                          onDragEnd={handleDirectionsDragEnd}
                          onPlaceClick={handlePlaceClick}
                        />
                      )}

                      {/* End location input */}
                      <LocationInput
                        value={endLocation}
                        onChange={(value) => handleLocationInputChange(value, 'end')}
                        onLocationSelect={(location, address) => handleLocationSelect(location, address, 'end')}
                        placeholder="Choose destination"
                        icon="place"
                        iconColor="#FF3B30"
                        isStart={false}
                        inputRef={endLocationInputRef}
                        disabled={isChatOpen}
                        onFocus={() => {
                          if (!isChatOpen) {
                            setIsSelectingLocation('end');
                          }
                        }}
                        onBlur={() => {
                          // Delay to allow click events to fire
                          setTimeout(() => setIsSelectingLocation(null), 200);
                        }}
                      />

                      {/* Selection hint */}
                      {isSelectingLocation && (
                        <div className="text-xs text-blue-600 px-2 py-1 bg-blue-50 rounded-lg">
                          Click on the map to select {isSelectingLocation === 'start' ? 'starting' : 'end'} point
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Map action buttons, stacked under zoom controls */}
          <div className="absolute right-2 top-[57%] z-[5] flex flex-col items-end gap-2">
            {(routePolyline || (routeSuggestions && routeSuggestions.length > 0) || (startLocationCoords && endLocationCoords)) && (
              <>
              <button
                type="button"
                onClick={() => setShowResetSessionConfirmDialog(true)}
                title="Start a new session"
                aria-label="Restart session and return to the first page"
                className="bg-white border-2 border-gray-200 text-gray-800 rounded-full p-3 shadow-lg flex items-center justify-center hover:bg-gray-50 active:bg-gray-100 transition-colors"
              >
                <span className="material-symbols-outlined text-[22px] text-gray-800" aria-hidden>
                  restart_alt
                </span>
              </button>
              <button
                type="button"
                onClick={handleSaveJourney}
                title="Save this route to your account. Open it anytime from Profile → Saved Journeys."
                aria-label="Save journey for later. Find saved routes in Profile, Saved Journeys."
                className={[
                  'border-2 rounded-full p-3 shadow-lg flex items-center justify-center active:bg-gray-100 transition-colors',
                  isCurrentJourneySaved
                    ? 'bg-blue-600 border-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-white border-gray-200 text-gray-800 hover:bg-gray-50',
                ].join(' ')}
              >
                <span
                  className={`material-symbols-outlined text-[22px] ${
                    isCurrentJourneySaved ? 'text-white' : 'text-gray-800'
                  }`}
                  style={isCurrentJourneySaved ? { fontVariationSettings: "'FILL' 1" } : undefined}
                  aria-hidden
                >
                  bookmark
                </span>
              </button>
              <button
                onClick={handleGoClick}
                className={[
                  'w-[52px] h-[52px] rounded-full shadow-lg transition-colors font-semibold text-sm flex items-center justify-center text-white',
                  activeRouteSuggestion === -1
                    ? 'bg-black hover:bg-gray-900 active:bg-gray-800'
                    : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800',
                ].join(' ')}
                title="Start Journey"
              >
                <span className="text-[15px] font-bold leading-none text-white">Go!</span>
              </button>
              </>
            )}
            {location && (
              <button
                onClick={() => setMapCenter({ lat: location.latitude, lng: location.longitude })}
                className="bg-white border-2 border-gray-200 rounded-full w-[52px] h-[52px] shadow-lg flex items-center justify-center hover:bg-gray-50 active:bg-gray-100 transition-colors"
                title="Localise me"
                aria-label="Center map on my location"
              >
                <span className="material-symbols-outlined text-blue-600">my_location</span>
              </button>
            )}
          </div>
          </>
        )}

        {savedJourneyMessage && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center pointer-events-none px-4">
            <div
              className={`rounded-xl px-4 py-3 shadow-lg text-sm font-semibold ${
                savedJourneyMessageTone === 'error'
                  ? 'bg-red-600 text-white'
                  : 'bg-green-600 text-white'
              }`}
            >
              {savedJourneyMessage}
            </div>
          </div>
        )}
      </div>

      {/* Floating Chat Button (FAB) - draggable, only show when chat is closed and on map tab */}
      {chatEnabled && activeTab === 'map' && !isChatOpen && (
        <button
          ref={chatButtonRef}
          onTouchStart={(e) => {
            const touch = e.touches[0];
            const rect = chatButtonRef.current?.getBoundingClientRect();
            if (rect) {
              isDraggingChatButtonRef.current = false;
              setIsDraggingChatButton(false);
              setDragStartPos({
                x: touch.clientX - (rect.left + rect.width / 2),
                y: touch.clientY - (rect.top + rect.height / 2)
              });
            }
          }}
          onTouchMove={(e) => {
            // Only prevent default if we've moved enough to be considered dragging
            const touch = e.touches[0];
            const moveThreshold = 10; // pixels
            const moved = Math.abs(touch.clientX - dragStartPos.x) > moveThreshold ||
                         Math.abs(touch.clientY - dragStartPos.y) > moveThreshold;

            if (moved) {
              e.preventDefault();
              isDraggingChatButtonRef.current = true;
              setIsDraggingChatButton(true);
              const buttonSize = 56; // 14 * 4 = 56px (w-14 h-14)
              const bottomNavHeight = 80;
              const newX = Math.max(buttonSize / 2, Math.min(window.innerWidth - buttonSize / 2, touch.clientX - dragStartPos.x));
              const newY = Math.max(buttonSize / 2, Math.min(window.innerHeight - buttonSize / 2 - bottomNavHeight, touch.clientY - dragStartPos.y));
              setChatButtonPosition({
                x: newX - buttonSize / 2,
                y: window.innerHeight - newY - buttonSize / 2 - bottomNavHeight
              });
            }
          }}
          onTouchEnd={(e) => {
            const wasDragging = isDraggingChatButtonRef.current;
            isDraggingChatButtonRef.current = false;
            setIsDraggingChatButton(false);
            if (!wasDragging) {
              // If we weren't dragging, open chat
              e.preventDefault();
              setIsChatOpen(true);
            }
          }}
          onMouseDown={(e) => {
            const rect = chatButtonRef.current?.getBoundingClientRect();
            if (rect) {
              isDraggingChatButtonRef.current = false;
              setIsDraggingChatButton(false);
              setDragStartPos({
                x: e.clientX - (rect.left + rect.width / 2),
                y: e.clientY - (rect.top + rect.height / 2)
              });
            }

            const buttonSize = 56;
            const bottomNavHeight = 80;

            const handleMouseMove = (e: MouseEvent) => {
              isDraggingChatButtonRef.current = true;
              setIsDraggingChatButton(true);
              const newX = Math.max(buttonSize / 2, Math.min(window.innerWidth - buttonSize / 2, e.clientX - dragStartPos.x));
              const newY = Math.max(buttonSize / 2, Math.min(window.innerHeight - buttonSize / 2 - bottomNavHeight, e.clientY - dragStartPos.y));
              setChatButtonPosition({
                x: newX - buttonSize / 2,
                y: window.innerHeight - newY - buttonSize / 2 - bottomNavHeight
              });
            };

            const handleMouseUp = () => {
              const wasDragging = isDraggingChatButtonRef.current;
              isDraggingChatButtonRef.current = false;
              setIsDraggingChatButton(false);
              if (!wasDragging) {
                // If we weren't dragging, open chat
                setIsChatOpen(true);
              }
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
          }}
          onClick={(e) => {
            // Only open chat if we weren't dragging
            if (!isDraggingChatButtonRef.current) {
              e.preventDefault();
              e.stopPropagation();
              setIsChatOpen(true);
            }
          }}
          className="fixed w-14 h-14 bg-blue-600 text-white rounded-full shadow-lg z-50 hover:bg-blue-700 active:scale-95 transition-all flex items-center justify-center cursor-pointer"
          style={{
            left: `${chatButtonPosition.x}px`,
            bottom: `${chatButtonPosition.y}px`,
            transition: isDraggingChatButton ? 'none' : 'left 0.2s, bottom 0.2s',
          }}
        >
          <span className="material-symbols-outlined text-[28px]">chat</span>
        </button>
      )}

      {/* Chat Modal/Panel */}
      {chatEnabled && isChatOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 transition-opacity"
            onClick={closeChatSavingScroll}
          />

          {/* Chat Panel */}
          <div
            className={`relative w-full max-w-lg bg-white rounded-t-3xl shadow-2xl animate-slide-up flex flex-col transition-all duration-300 ${
              isChatExpanded ? 'max-h-[95vh]' : 'max-h-[85vh]'
            }`}
          >
            {/* Swipe handle */}
            <div
              className="flex justify-center pt-2 pb-1 cursor-grab active:cursor-grabbing"
              onTouchStart={(e) => {
                setChatSwipeStartY(e.touches[0].clientY);
              }}
              onTouchMove={(e) => {
                const currentY = e.touches[0].clientY;
                const diff = chatSwipeStartY - currentY;

                // Swipe up to expand (diff > 50)
                if (diff > 50 && !isChatExpanded) {
                  setIsChatExpanded(true);
                }
                // Swipe down to collapse (diff < -50)
                else if (diff < -50 && isChatExpanded) {
                  setIsChatExpanded(false);
                }
              }}
              onMouseDown={(e) => {
                setChatSwipeStartY(e.clientY);
              }}
              onMouseMove={(e) => {
                if (e.buttons === 1) { // Left mouse button is pressed
                  const currentY = e.clientY;
                  const diff = chatSwipeStartY - currentY;

                  if (diff > 50 && !isChatExpanded) {
                    setIsChatExpanded(true);
                  }
                  else if (diff < -50 && isChatExpanded) {
                    setIsChatExpanded(false);
                  }
                }
              }}
            >
              <div className="w-10 h-1 bg-gray-300 rounded-full"></div>
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="material-symbols-outlined text-blue-600">smart_toy</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">RouteX Assistant</h3>
                  <p className="text-xs text-gray-500">AI Travel Planner</p>
                </div>
              </div>
              <button
                onClick={closeChatSavingScroll}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <span className="material-symbols-outlined text-gray-500">close</span>
              </button>
            </div>

            {/* Chat messages */}
            <div
              ref={chatMessagesContainerRef}
              className={`flex-1 overflow-y-auto px-4 py-3 custom-scrollbar min-h-[300px] transition-all duration-300 ${
                isChatExpanded ? 'max-h-[75vh]' : 'max-h-[50vh]'
              }`}
            >
              {messages.map((msg) => (
                <ChatMessage
                  key={msg.id}
                  message={msg}
                  onPlaceSelect={handlePlaceSelect}
                  onRouteStopOpenDetails={handlePlaceClick}
                  canRemoveRouteStop={() => true}
                  onRouteStopRemove={(p, routeIndex) =>
                    handleRemovePlaceFromRoute(routeIndex, p.place_id)
                  }
                  onOpenMapFromRouteSuggestions={(routeIndex) => {
                    setActiveTab('map');
                    selectSuggestedRouteOption(routeIndex);
                    setIsLocationPanelCollapsed(false);
                    closeChatSavingScroll();
                  }}
                />
              ))}

              {/* Live draft: show current input text in the chat while typing */}
              {inputText.trim() && (
                <div className="flex justify-end mb-4 px-2 opacity-90">
                  <div className="max-w-[85%] bg-blue-600 text-white rounded-2xl rounded-br-sm px-4 py-3">
                    <p className="text-[15px] leading-relaxed text-white whitespace-pre-wrap break-words">
                      {inputText}
                    </p>
                    <p className="text-[10px] mt-2 text-blue-100">Typing...</p>
                  </div>
                </div>
              )}

              {isLoading && (
                <div className="mb-3 px-2 flex justify-start">
                  <AgentStatusBar message={LOADING_STATUS_MESSAGES[loadingStatusIndex]} />
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Chat input */}
            <div className="px-4 py-3 border-t border-gray-100 bg-white rounded-b-3xl">
              <form onSubmit={handleFormSubmit} className="flex items-center gap-2">
                {/* Mute/Speaker button */}
                <button
                  type="button"
                  onClick={toggleMute}
                  className={`p-2 rounded-full transition-colors flex-shrink-0 ${
                    isMuted
                      ? 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                      : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                  }`}
                  title={isMuted ? 'Unmute audio' : 'Mute audio'}
                >
                  <span className="material-symbols-outlined text-[20px] block">
                    {isMuted ? 'volume_off' : 'volume_up'}
                  </span>
                </button>

                {/* Voice input button */}
                <button
                  type="button"
                  onMouseDown={startVoiceInput}
                  onMouseUp={stopVoiceInput}
                  onMouseLeave={stopVoiceInput}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    startVoiceInput();
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    stopVoiceInput();
                  }}
                  className={`p-2 rounded-full transition-colors flex-shrink-0 ${
                    isListening
                      ? 'bg-red-500 text-white animate-pulse'
                      : 'bg-green-100 text-green-600 hover:bg-green-200'
                  }`}
                  title="Hold to speak"
                >
                  <span className="material-symbols-outlined text-[20px] block">
                    mic
                  </span>
                </button>

                {/* Text input container */}
                <div className="relative flex-1">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={(e) => {
                      // Prevent Enter key from bubbling to other inputs or triggering other forms
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        // Submit the form programmatically
                        handleFormSubmit(e as any);
                      } else {
                        // Stop propagation for all keys to prevent interference
                        e.stopPropagation();
                      }
                    }}
                    onKeyPress={(e) => {
                      // Additional protection - stop all keypress events from bubbling
                      e.stopPropagation();
                    }}
                    placeholder={
                      user?.uid && !canUseAppNow
                        ? "Upgrade to continue..."
                        : "Ask for directions, places..."
                    }
                    disabled={user?.uid ? !canUseAppNow : false}
                    className={`w-full bg-gray-100 text-gray-900 rounded-full py-3 pl-5 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500/50 border border-transparent transition-all placeholder-gray-500 text-[15px] ${
                      user?.uid && !canUseAppNow ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    autoComplete="off"
                    data-form-type="chat"
                  />

                  {/* Send button */}
                  <button
                    type="submit"
                    disabled={!inputText.trim() || isLoading || (user?.uid ? !canUseAppNow : false)}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full transition-colors ${
                      inputText.trim() && (user?.uid ? canUseAppNow : true)
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-transparent text-gray-400'
                    }`}
                  >
                    <span className="material-symbols-outlined text-[20px] block">send</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Main content layer - for non-map tabs */}
      {activeTab !== 'map' && (
        <main className="flex-1 bg-gray-50/50 overflow-y-auto custom-scrollbar pt-safe-top pb-[80px]">
          {renderTabContent()}
        </main>
      )}

      {/* Bottom navigation */}
      <div className="absolute bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200">
        <BottomNav
          tabs={bottomTabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>

      {/* Apple leg-by-leg navigator helper */}
      {appleLegPoints.length >= 2 && currentAppleLegIndex < appleLegPoints.length - 1 && (
        <div className="fixed left-4 right-4 bottom-24 z-[55]">
          <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Apple Maps navigation</p>
            <p className="text-sm font-medium text-gray-800 mb-2">
              Leg {currentAppleLegIndex + 1} of {appleLegPoints.length - 1}
            </p>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => {
                  const fromPoint = appleLegPoints[currentAppleLegIndex];
                  const toPoint = appleLegPoints[currentAppleLegIndex + 1];
                  if (!fromPoint || !toPoint) return;
                  const mode = tripConfig?.travelMode || 'DRIVING';
                  const params = new URLSearchParams();
                  params.set('saddr', fromPoint);
                  params.set('daddr', toPoint);
                  params.set('dirflg', mode === 'WALKING' ? 'w' : mode === 'TRANSIT' ? 'r' : 'd');
                  window.open(`https://maps.apple.com/?${params.toString()}`, '_blank');
                }}
                className="flex-1 py-2.5 px-3 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                Redo current step
              </button>
              <button
                onClick={() => {
                  const nextLegIndex = currentAppleLegIndex + 1;
                  if (nextLegIndex >= appleLegPoints.length - 1) {
                    setAppleLegPoints([]);
                    setCurrentAppleLegIndex(0);
                    return;
                  }
                  const fromPoint = appleLegPoints[nextLegIndex];
                  const toPoint = appleLegPoints[nextLegIndex + 1];
                  const mode = tripConfig?.travelMode || 'DRIVING';
                  const params = new URLSearchParams();
                  params.set('saddr', fromPoint);
                  params.set('daddr', toPoint);
                  params.set('dirflg', mode === 'WALKING' ? 'w' : mode === 'TRANSIT' ? 'r' : 'd');
                  window.open(`https://maps.apple.com/?${params.toString()}`, '_blank');
                  setCurrentAppleLegIndex(nextLegIndex);
                }}
                className="flex-1 py-2.5 px-3 bg-black text-white rounded-lg text-sm font-semibold hover:bg-gray-900"
              >
                Next stop in Apple Maps
              </button>
              <button
                onClick={() => {
                  setAppleLegPoints([]);
                  setCurrentAppleLegIndex(0);
                }}
                className="py-2.5 px-3 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                End
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Go! confirmation: "Book before journey?" */}
      {showGoConfirmDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowGoConfirmDialog(false)}
            aria-hidden
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-scale-in">
            <p className="text-gray-800 text-center mb-6">
              Do you want me to book (if possible) before starting your journey?
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleGoConfirmYes}
                className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium transition-colors"
              >
                Yes
              </button>
              <button
                onClick={handleGoConfirmNo}
                className="flex-1 py-3 px-4 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 font-medium transition-colors"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restart session confirmation */}
      {showResetSessionConfirmDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={handleResetSessionConfirmNo}
            aria-hidden
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-scale-in">
            <p className="text-gray-800 text-center mb-6">
              Do you want to close the session and return to the welcome page?
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleResetSessionConfirmYes}
                className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium transition-colors"
              >
                Yes
              </button>
              <button
                onClick={handleResetSessionConfirmNo}
                className="flex-1 py-3 px-4 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 font-medium transition-colors"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Booking placeholder (future tools) */}
      {showBookingPlaceholder && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowBookingPlaceholder(false)}
            aria-hidden
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-scale-in">
            <div className="text-center mb-6">
              <span className="material-symbols-outlined text-4xl text-blue-500 mb-3 block">event_available</span>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Book before journey</h2>
              <p className="text-gray-600 text-sm">
                Booking tools will be available here in a future update. You can continue and choose your navigation app now.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowBookingPlaceholder(false)}
                className="flex-1 py-3 px-4 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 font-medium transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowBookingPlaceholder(false);
                  setShowNavigationAppDialog(true);
                }}
                className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation app chooser */}
      {showNavigationAppDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowNavigationAppDialog(false)}
            aria-hidden
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-scale-in">
            <p className="text-gray-800 text-center mb-5 font-medium">
              Open your journey in which app?
            </p>
            <div className="space-y-2">
              <button
                onClick={() => {
                  setShowNavigationAppDialog(false);
                  handleStartJourney('google');
                }}
                className="w-full py-3 px-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium transition-colors"
              >
                Google Maps
              </button>
              <button
                onClick={() => {
                  setShowNavigationAppDialog(false);
                  handleStartJourney('apple');
                }}
                className="w-full py-3 px-4 bg-black text-white rounded-xl hover:bg-gray-900 font-medium transition-colors"
              >
                Apple Maps
              </button>
              <button
                onClick={() => setShowNavigationAppDialog(false)}
                className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Prompt Modal */}
      {showUpgradePrompt && user?.uid && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowUpgradePrompt(false)}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-scale-in">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                <span className="material-symbols-outlined text-white text-3xl">lock</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Upgrade Required</h2>
              <p className="text-gray-600">
                You've used all your free responses. Upgrade to continue using RouteX.
              </p>
            </div>

            <div className="space-y-3 mb-6">
              {/* Standard Plan */}
              <button
                onClick={() => {
                  if (user?.uid) {
                    void (async () => {
                      await updateAccountType(user.uid, 'Professional');
                      setCanUseAppNow(true);
                      setShowUpgradePrompt(false);
                      const successMsg: Message = {
                        id: Date.now().toString(),
                        role: Role.ASSISTANT,
                        text: "✅ Your account has been upgraded to Standard! You can now continue using RouteX.",
                        timestamp: Date.now()
                      };
                      setMessages(prev => [...prev, successMsg]);
                    })();
                  }
                }}
                className="w-full p-4 bg-blue-50 border-2 border-blue-200 rounded-xl hover:bg-blue-100 transition-colors text-left"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Standard</h3>
                    <p className="text-sm text-gray-600">Unlimited responses</p>
                  </div>
                  <span className="text-blue-600 font-semibold">$14.99/mo</span>
                </div>
              </button>

              {/* Premium Plan */}
              <button
                onClick={() => {
                  if (user?.uid) {
                    void (async () => {
                      await updateAccountType(user.uid, 'Premium');
                      setCanUseAppNow(true);
                      setShowUpgradePrompt(false);
                      const successMsg: Message = {
                        id: Date.now().toString(),
                        role: Role.ASSISTANT,
                        text: "✅ Your account has been upgraded to Premium! You can now continue using RouteX.",
                        timestamp: Date.now()
                      };
                      setMessages(prev => [...prev, successMsg]);
                    })();
                  }
                }}
                className="w-full p-4 bg-purple-50 border-2 border-purple-200 rounded-xl hover:bg-purple-100 transition-colors text-left"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Premium</h3>
                    <p className="text-sm text-gray-600">Unlimited responses + Advanced features</p>
                  </div>
                  <span className="text-purple-600 font-semibold">$19.99/mo</span>
                </div>
              </button>
            </div>

            <button
              onClick={() => {
                setShowUpgradePrompt(false);
                onBackToWelcome();
              }}
              className="w-full py-3 px-4 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
            >
              Maybe Later
            </button>
          </div>
        </div>
      )}

      {/* Place Details Modal */}
      <PlaceDetailsModal
        place={selectedPlace}
        isOpen={isPlaceDetailsOpen}
        onClose={() => {
          setIsPlaceDetailsOpen(false);
          setSelectedPlace(null);
        }}
        onRemove={handleRemovePlace}
        onAddToRoute={handleAddPlaceToRoute}
        isInRoute={!!selectedPlace && suggestedPlaces.some((p) => p.place_id === selectedPlace.place_id)}
      />
    </div>
  );
};

// Root App Component with AuthProvider
const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
